import { type JSX, useCallback, useEffect, useRef, useState } from 'react';

import { type Benutzer, type Recht, ich as holeIch } from './api/anmeldung';
import { ApiFehler } from './api/fehler';
import { Maskenhilfe } from './komponenten/Maskenhilfe';
import { Auffangnetz } from './komponenten/Auffangnetz';
import { Sinnbild } from './komponenten/Sinnbild';
import { glockeneintraege } from './glocke';
import { Meldung } from './komponenten/Meldung';
import { SeitenkontextAnbieter, type Seitenzusatz } from './komponenten/Seitenkontext';
import { Bereichsseite } from './komponenten/Bereichsseite';
import { type Aktenwege } from './komponenten/Aktenwege';
import {
  BEREICHSGRUPPEN,
  Rahmen,
  gruppeVonBereich,
  sichtbareGruppen,
  type Kopf,
} from './komponenten/Rahmen';
import { Kundenliste } from './kunde/Kundenliste';
import { Webanfragen } from './kunde/Webanfragen';
import { Kontozuordnungen } from './kunde/Kontozuordnungen';
import { KundeAnlegen } from './kunde/KundeAnlegen';
import { Kundenakte } from './kunde/Kundenakte';
import { DublettenZusammenfuehren, alsFrist } from './kunde/DublettenZusammenfuehren';
import { Arbeitsplatz } from './start/Arbeitsplatz';
import { Warengruppen } from './produkt/Warengruppen';
/* Nur der Typ — siehe den Kopf von @crm/kontrakte (B8). */
import type { Bereichsgruppenkennung, Bereichskennung } from '@crm/kontrakte';

import { Arbeitsliste } from './buchung/Arbeitsliste';
import { Absagen } from './buchung/Absagen';
import { Honorartabelle } from './ressource/Honorartabelle';
import { Preislisten } from './produkt/Preislisten';
import { Rabattkonditionen } from './produkt/Rabattkonditionen';
import { Artikelliste } from './produkt/Artikelliste';
import { Bausteine } from './produkt/Bausteine';
import { ArtikelAnlegen } from './produkt/ArtikelAnlegen';
import { Artikelakte } from './produkt/Artikelakte';
import { Angebotsliste } from './angebot/Angebotsliste';
import { AuftragAnlegen } from './auftrag/AuftragAnlegen';
import { Auftragsakte } from './auftrag/Auftragsakte';
import { Auftragsliste } from './auftrag/Auftragsliste';
import { Fakturierungsliste } from './auftrag/Fakturierungsliste';
import { Belegvorlagen } from './verwaltung/Belegvorlagen';
import { Rezensionen } from './rezension/Rezensionen';
import { folgeaktionErgebnis } from './api/support';
import { Posteingang } from './support/Posteingang';
import { postfachVorbelegung } from './support/postfachvorbelegung';
import { MeineWiedervorlagen } from './kunde/Wiedervorlagen';
import { MeinKonto } from './anmeldung/MeinKonto';
import { Systemzustand } from './systemzustand/Systemzustand';
import { Buchungsvorgang } from './buchung/Buchungsvorgang';
import { Besetzung } from './ressource/Besetzung';
import { Ressourcen } from './ressource/Ressourcen';
import { Empfang } from './planung/Empfang';
import { Trainingsplanung } from './planung/Trainingsplanung';
import { Gebuehrenstaffeln } from './verwaltung/Gebuehrenstaffeln';
import { Datenschutz } from './verwaltung/Datenschutz';
import { Betriebspruefung } from './betriebspruefung/Betriebspruefung';
import { Baukasten } from './bericht/Baukasten';
import { Berichtsliste } from './bericht/Berichtsliste';
import { Berichtsversanduebersicht } from './bericht/Berichtsversand';
import { Einstellungen } from './verwaltung/Einstellungen';
import { Widerrufe } from './buchung/Widerrufe';
import { Kalender } from './verwaltung/Kalender';
import { Standorte } from './verwaltung/Standorte';
import { Uebernahme } from './verwaltung/Uebernahme';
import { Rechtstexte } from './verwaltung/Rechtstexte';
import { Textbausteine } from './verwaltung/Textbausteine';
import { Terminakte } from './termin/Terminakte';
import { Termine } from './termin/Termine';
import { Benutzerakte } from './verwaltung/Benutzerakte';
import { Benutzerliste } from './verwaltung/Benutzerliste';
import { Bestaetigungen } from './verwaltung/Bestaetigungen';
import { Erechnung } from './verwaltung/Erechnung';
import { Ki } from './verwaltung/Ki';
import { Geschaeftsdaten } from './verwaltung/Geschaeftsdaten';
import { Navision } from './verwaltung/Navision';
import { AbTablet } from './komponenten/AbTablet';
import { Postfaecher } from './verwaltung/Postfaecher';
import { Portalzugaenge } from './verwaltung/Portalzugaenge';
import { Checkingeraete } from './verwaltung/Checkingeraete';
import { OffenePosten, Rechnungsakte, Rechnungsliste } from './rechnung/Rechnungen';
import { BgAbrechnungsmaske, darfBgKennzeichenSetzen } from './rechnung/BgZuschuss';
import { NeueRechnung } from './rechnung/NeueRechnung';
import {
  RahmenauftragAnlegen,
  Rahmenauftragsakte,
  Rahmenauftragsliste,
} from './auftrag/Rahmenauftraege';
import { Suchfenster } from './suche/Suchfenster';
import { sichtbareArten, vermerken, type Felder, type Suchart } from './api/suche';
import { AngebotAnlegen } from './angebot/AngebotAnlegen';
import { type Glocke, glocke as holeGlocke } from './api/uebersicht';
import { Angebotsakte } from './angebot/Angebotsakte';
import { Optionsanfragen } from './angebot/Optionsanfragen';
import type { Kundenart } from './api/kunden';
import {
  bereichVon,
  buchungslisteVon,
  gesetzteFilter,
  maskeAus,
  pfadVon,
  schluesselVon,
  type Einstieg,
  type Maske,
} from './wegweiser';

/**
 * Die Maske zur aktuellen Adresse — oder der Arbeitsplatz (12.09.2026).
 *
 * SIE STEHT AUF MODULEBENE, damit `useState` sie als Anfangswert bekommt: Ein
 * Anfangswert aus dem Rumpf der Komponente liefe bei jedem Bild erneut, und ein
 * `useEffect` danach zeigte für einen Augenblick den Arbeitsplatz, bevor die
 * richtige Maske einspringt.
 */
/**
 * Die Kennungen der Menuegruppen — aus der Navigation dieser Oberflaeche.
 *
 * NICHT `BEREICHSGRUPPENKENNUNGEN` aus @crm/kontrakte: Das Paket ist CommonJS,
 * und ein WERTimport daraus zoege zod in das Buendel (siehe den Kopf von
 * @crm/kontrakte und `buendeltrennung.test.ts`). `BEREICHSGRUPPEN` in Rahmen.tsx
 * ist gegen dieselbe Aufzaehlung typgeprueft — dieselbe Wahrheit, ohne Fracht.
 */
const GRUPPENKENNUNGEN: readonly Bereichsgruppenkennung[] = BEREICHSGRUPPEN.map(
  (g) => g.kennung,
);

/** Pfad und Abfrageteil — das, was `pfadVon` erzeugt. */
function adresse(): string {
  return window.location.pathname + window.location.search;
}

function ausAdresse(): Maske {
  return (
    /* Mit Abfrageteil: Die Buchungsliste trägt ihre Filter darin (Auftrag CRM-70). */
    maskeAus(adresse(), BEREICH_ZU_MASKE, GRUPPENKENNUNGEN)
    ?? { art: 'arbeitsplatz' }
  );
}

/** Gruppe und Punkt zu einer Maske — die Gruppe folgt dem Punkt. */
function stelleFuer(maske: Maske): {
  readonly gruppe: string | null;
  readonly bereich: string | null;
} {
  if (maske.art === 'bereichsseite') return { gruppe: maske.gruppe, bereich: null };
  const bereich = bereichVon(maske);
  return bereich === null
    ? { gruppe: null, bereich: null }
    : { gruppe: gruppeVonBereich(bereich), bereich };
}

/**
 * Welche Maske hinter welchem Menuepunkt liegt.
 *
 * ============================================================================
 * EIN VOLLSTAENDIGER RECORD, UND DAS IST DER PUNKT (B8, 12.09.2026)
 * ============================================================================
 *
 * `Record<Bereichskennung, Maske>` statt `Record<string, Maske>`: Ein Bereich,
 * der in @crm/kontrakte steht und hier fehlt, ist ein TYPFEHLER beim
 * Uebersetzen. Vorher war er eine Maske, die es nicht gibt — und genau das ist
 * am 11.09.2026 passiert, als aus `ressourcen` zwei Punkte wurden und eine
 * Aufgabe der Startseite ins Leere sprang.
 *
 * DESHALB STEHEN AUCH DIE UNGEBAUTEN HIER. Ein Bereich ohne Maske traegt einen
 * Platzhalter, der sagt, WAS dort kommt (B3) — so stand bis Auftrag #24
 * `auswertungen` hier; heute hat jeder Bereich seine Maske.
 * Einen Bereich einfach weglassen kann dieser Record nicht — und das ist die
 * Absicht: Ein weggelassener Bereich waere wieder ein stiller Sprung ins Leere.
 */
const BEREICH_ZU_MASKE: Record<Bereichskennung, Maske> = {
  dashboard: { art: 'arbeitsplatz' },
  /* KUN-15: „Meine Wiedervorlagen". */
  wiedervorlagen: { art: 'wiedervorlagen' },
  kunden: { art: 'kundenliste' },
  privatkunden: { art: 'kundenliste', einstieg: 'service' },
  angebote: { art: 'angebotsliste' },
  /* ANG-38: die Arbeitsliste der Disposition — seit 0197 gebaut. */
  anfragen: { art: 'optionsanfragen' },
  auftraege: { art: 'auftragsliste' },
  rechnungen: { art: 'rechnungsliste' },
  termine: { art: 'termine' },
  trainingsplanung: { art: 'trainingsplanung' },
  besetzung: { art: 'besetzung' },
  assets: { art: 'assets' },
  trainer: { art: 'trainerstamm' },
  buchungen: { art: 'buchungsvorgang' },
  arbeitsliste: { art: 'arbeitsliste' },
  absagen: { art: 'absagen' },
  widerrufe: { art: 'widerrufe' },
  empfang: { art: 'empfang' },
  rezensionen: { art: 'rezensionen' },
  posteingang: { art: 'posteingang' },
  produkte: { art: 'artikelliste' },
  bausteine: { art: 'bausteine' },
  honorare: { art: 'honorare' },
  /* AUS-16 (Auftrag #24): an der Stelle des Platzhalters „Auswertungen". */
  berichte: { art: 'berichtsliste' },
  verwaltung: { art: 'geschaeftsdaten' },
  belege: { art: 'belegvorlagen' },
  umsysteme: { art: 'postfaecher' },
  benutzer: { art: 'benutzerliste' },
  datenschutz: { art: 'datenschutz' },
  betriebspruefung: { art: 'betriebspruefung' },
  systemzustand: { art: 'systemzustand' },
};

/**
 * Worauf die Kundenliste je Einstieg steht (Tab. 11 i.d.F. AeA-01, Auftrag #9).
 *
 * Der Vertrieb betreut Firmen und oeffentlichen Dienst, der Service die
 * Privatkunden. Eine Vorbelegung und keine Sperre — die Liste zeigt jede Art
 * als abwaehlbare Marke.
 */
/**
 * PLANEN UND VERWALTEN AB TABLET (Auftrag CRM-106, ÄA-63). Diese Masken sind
 * auf dem Telefon nur zum Lesen da (`AbTablet`); die Rechte bleiben, wie sie
 * sind. Die Ressourcenkonfiguration hüllt `Ressourcen` selbst, die
 * Trainingsplanung zeigt statt der Zeitachse eine Tagesliste.
 *
 * Der Berichtsbaukasten steht nicht mehr darin (Auftrag CRM-110, Nachtrag zu
 * ÄA-63 vom 07.10.2026): Er zählt zu den einfachen Handgriffen und ist auf
 * dem Telefon bearbeitbar — wählen, speichern, umbenennen, löschen.
 */
const NUR_AB_TABLET: ReadonlySet<Maske['art']> = new Set<Maske['art']>([
  'honorare',
  'navision',
  'einstellungen',
  'angebotAnlegen',
  'auftragAnlegen',
  'rahmenAnlegen',
  'rechnungAnlegen',
  /* Auftrag CRM-175: das Anlegen einer Buchung, eigene Seite seit diesem Auftrag. */
  'buchungAnlegen',
]);

const VORBELEGUNG: Readonly<Record<Einstieg, readonly Kundenart[]>> = {
  vertrieb: ['geschaeft', 'oeffentlich'],
  service: ['privat'],
};

/**
 * Den Einstieg einer Kundenmaske in die naechste mitnehmen.
 *
 * Ohne Einstieg bleibt die Maske ohne — der Vertrieb ist der Rueckfall, und
 * seine Adressen bleiben, wie sie waren (`/kunden/<id>`).
 */
function mitEinstieg(maske: { readonly einstieg?: Einstieg }): { readonly einstieg?: Einstieg } {
  return maske.einstieg === 'service' ? { einstieg: 'service' } : {};
}

/**
 * Die angemeldete Oberflaeche: Seitenraster und die Masken darin.
 *
 * Die Navigation entsteht aus den WIRKSAMEN RECHTEN und nicht aus der Rolle
 * (Pflichtenheft 4.3). Sie werden hier einmal geholt und beim Wechsel der
 * Maske nicht erneut gelesen; BEN-05 verlangt, dass eine Rechteaenderung
 * spaetestens beim naechsten Seitenaufruf wirkt — das Neuladen der Seite ist
 * genau dieser Aufruf.
 *
 * Gebaut sind Start, Kunden, Produkte und Angebote. Die uebrigen Bereiche stehen im Menue, weil
 * ihre Rechte bestehen, und sagen beim Oeffnen, woran sie haengen. Das ist
 * ehrlicher als ein Menue, das mit jedem Modul wandert: Der Maskenschnitt aus
 * Kapitel 03 ist verbindlich, die Reihenfolge des Bauens ist es nicht.
 */
export function Arbeitsflaeche({
  benutzer,
  beiAbmelden,
}: {
  readonly benutzer: Benutzer;
  readonly beiAbmelden: () => void;
}): JSX.Element {
  const [rechte, setzeRechte] = useState<readonly Recht[]>([]);
  /**
   * Wo man in der Navigation steht — Gruppe UND Punkt in einem Zustand.
   *
   * Zwei getrennte Zustaende waeren zwei, die auseinanderlaufen: Ein Sprung von
   * der Startseite setzt den Punkt, und die Gruppe muesste jemand nachziehen.
   * Hier folgt sie ihm.
   *
   * `bereich: null` heisst: Die Bereichsseite der Gruppe ist offen, kein
   * einzelner Punkt ist der aktuelle.
   */
  const [stelle, setzeStelle] = useState<{
    readonly gruppe: string | null;
    readonly bereich: string | null;
  }>(() => stelleFuer(ausAdresse()));
  const [maske, setzeMaskeRoh] = useState<Maske>(ausAdresse);
  /**
   * WAS DIE MASKE IN DEN SEITENKOPF MELDET (ÄA-41): der Zähler einer Liste und
   * die Handlungen einer Erfassung (`Seitenkontext.tsx`).
   */
  const [zusatz, setzeZusatz] = useState<Seitenzusatz>({});
  /*
   * KEIN ZURÜCKSETZEN BEIM MASKENWECHSEL: Die Effekte der neuen Maske laufen
   * VOR denen der Arbeitsfläche — ein Zurücksetzen hier löschte, was sie
   * gerade gemeldet hat. Die alte Maske nimmt ihren Zusatz beim Abbau selbst
   * zurück (`useZaehler`, `useKopfhandlungen`).
   */
  const meldeZusatz = useCallback((teil: Seitenzusatz) => setzeZusatz((z) => ({ ...z, ...teil })), []);

  /**
   * ==========================================================================
   * DIE MASKE SETZEN HEISST DIE ADRESSE SETZEN (12.09.2026)
   * ==========================================================================
   *
   * Auftraggeber: „der angezeigte link zeigt auf kennzahlen, egal auf welcher
   * seite man sich befindet, das führt dazu, dass man beim reload auf der
   * falschen seite landet."
   *
   * EINE KLAMMER UM DEN ZUSTANDSSETZER und nicht sechzig Stellen, die je zwei
   * Dinge tun: `setzeMaske` heißt an sechzig Stellen in dieser Datei, und jede
   * einzelne davon müsste sonst `pushState` mitziehen. Eine würde es vergessen,
   * und der Fehler wäre wieder da — an genau einer Seite.
   *
   * DIE STELLE FOLGT DER MASKE. Vorher setzte `wechsle` sie und die Sprünge in
   * eine Akte ließen sie stehen; jetzt sagt `bereichVon` für jede Maske, welcher
   * Punkt leuchtet. `null` heißt „nicht anfassen" — „Mein Konto" hängt an keinem
   * Punkt.
   *
   * `ersetzen` für den Weg, der keine Station ist: Wer aus der Kundenliste eine
   * Akte öffnet, will mit „zurück" in die Liste. Wer eine Meldung an derselben
   * Akte bekommt („Kunde angelegt"), will nicht dieselbe Akte zweimal in der
   * Geschichte haben.
   */
  const setzeMaske = useCallback((neu: Maske, ersetzen = false): void => {
    setzeMaskeRoh(neu);
    setzeStelle((vorher) => {
      const naechster = stelleFuer(neu);
      return naechster.bereich === null && neu.art !== 'bereichsseite'
        ? vorher
        : naechster;
    });

    const ziel = pfadVon(neu);
    /*
     * NUR WENN SICH DIE ADRESSE WIRKLICH AENDERT. Sonst legt jede Meldung, jede
     * Vorbelegung und jedes erneute Setzen derselben Maske einen Eintrag in die
     * Geschichte — und „zurück" braucht dann fünf Klicks für einen Schritt.
     */
    if (adresse() === ziel) return;
    if (ersetzen) window.history.replaceState(null, '', ziel);
    else window.history.pushState(null, '', ziel);
  }, []);

  /**
   * DER RÜCKWÄRTSPFEIL (12.09.2026).
   *
   * Ohne diesen Horcher schöbe „zurück" die Adresse zurück und ließe die Maske
   * stehen — der schlechtere Zustand als vorher, weil die Zeile dann etwas
   * anderes zeigt als der Bildschirm.
   *
   * Er setzt den Zustand DIREKT und nicht über `setzeMaske`: Der Browser hat die
   * Geschichte schon bewegt; ein `pushState` daraufhin legte einen neuen Eintrag
   * an, und man käme nie zurück.
   */
  useEffect(() => {
    const beiZurueck = (): void => {
      const neu = ausAdresse();
      setzeMaskeRoh(neu);
      setzeStelle((vorher) => {
        const naechster = stelleFuer(neu);
        return naechster.bereich === null && neu.art !== 'bereichsseite'
          ? vorher
          : naechster;
      });
    };
    window.addEventListener('popstate', beiZurueck);
    return () => window.removeEventListener('popstate', beiZurueck);
  }, []);

  /**
   * EINE ADRESSE, DIE ES NICHT GIBT, WIRD BERICHTIGT.
   *
   * Ein Lesezeichen von vorgestern oder ein Tippfehler landet auf dem
   * Arbeitsplatz — und die Zeile sagt dann auch `/dashboard`. Ohne dieses
   * `replaceState` behauptete sie weiter `/kennzahlen`, und der nächste Neuladen
   * wäre derselbe Sprung ins Leere.
   */
  useEffect(() => {
    const ziel = pfadVon(maske);
    if (adresse() !== ziel) {
      window.history.replaceState(null, '', ziel);
    }
    /*
     * NUR BEIM ERSTEN BILD, und `maske` steht deshalb absichtlich nicht in der
     * Abhängigkeitsliste: Danach hält `setzeMaske` die Adresse nach. Liefe dieser
     * Effekt bei jeder Maske, überschriebe er den Eintrag, den `pushState` gerade
     * angelegt hat — und „zurück" wäre wieder wirkungslos.
     */
  }, []);
  const [kopf, setzeKopf] = useState<Kopf>({ titel: 'Übersicht', kennzeichen: undefined });
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  /** M-07: das Suchfenster ueber der laufenden Maske. */
  const [suchfensterOffen, setzeSuchfenster] = useState(false);
  const [suchbareArten, setzeSuchbareArten] = useState<readonly Suchart[]>([]);
  /**
   * Die eigenen fälligen Einträge für die Glocke der Kopfzeile (Auftrag
   * CRM-170, ÄA-70) — Wiedervorlagen und Optionen, keine hausweiten Hinweise.
   *
   * `undefined`, solange sie nicht geladen sind oder die Abfrage scheitert —
   * dann steht die Glocke nicht da. Eine Glocke mit „0", die in Wahrheit nur
   * nicht antwortet, wäre die falsche Beruhigung.
   */
  const [glocke, setzeGlocke] = useState<Glocke | undefined>(undefined);

  useEffect(() => {
    let abgebrochen = false;
    const laden = (hintergrund: boolean): void => {
      /* Ein eigener, schmaler Endpunkt — nicht die Übersicht (CRM-165, CRM-170). */
      holeGlocke(hintergrund)
        .then((g) => {
          if (!abgebrochen) setzeGlocke(g);
        })
        .catch(() => undefined);
    };
    laden(false);
    /* Fünf Minuten: Fristen ändern sich über den Tag, nicht je Sekunde. Der Takt hält die Sitzung nicht wach (BEN-07). */
    const takt = setInterval(() => laden(true), 5 * 60 * 1000);
    return () => {
      abgebrochen = true;
      clearInterval(takt);
    };
  }, []);

  useEffect(() => {
    void holeIch()
      .then((auskunft) => setzeRechte(auskunft.rechte))
      .catch((ursache: unknown) => setzeFehler(ursache as ApiFehler));
    /*
     * Welche Arten das Suchfenster zeigen darf, entscheidet die API und nicht
     * die Oberflaeche: "Was der Benutzer nicht sehen darf, erscheint auch hier
     * nicht" (M-07). Eine Ableitung aus den Rechten waere eine zweite
     * Rechnung derselben Frage.
     */
    void sichtbareArten()
      .then(setzeSuchbareArten)
      .catch(() => setzeSuchbareArten([]));
  }, []);

  /**
   * In einen Bereich wechseln.
   *
   * Die Kennung kommt aus der Menuezeile und ist damit eine
   * `Bereichskennung` — der Record deckt sie vollstaendig ab (B8), es gibt
   * keinen unbekannten Bereich mehr. Der Rueckfall bleibt trotzdem stehen: Die
   * Startseite schickt ihre Sprungziele als Zeichenkette ueber die
   * Schnittstelle, und eine Antwort aus einer aelteren Fassung des Servers darf
   * die Oberflaeche nicht zerbrechen (ERR-08).
   */
  const wechsle = useCallback(
    (kennung: string): void => {
      /*
       * DIE STELLE WIRD NICHT MEHR HIER GESETZT. `setzeMaske` leitet sie aus der
       * Maske ab (`bereichVon`) — vorher stand sie an dieser Stelle UND die
       * Sprünge in eine Akte zogen sie über `stelleAuf` von Hand nach. Zwei Wege
       * für dieselbe Frage, und der zweite war der, den man vergisst.
       */
      /*
       * Auftrag CRM-95: Der Menüpunkt „Buchungen" ist der Weg zurück aus einer
       * Buchung und bringt die Filter mit, die sie aus der Liste mitnahm.
       */
      const ziel =
        kennung === 'buchungen'
          ? buchungslisteVon(maskeJetzt.current)
          : BEREICH_ZU_MASKE[kennung as Bereichskennung];
      setzeMaske(
        ziel ?? {
          art: 'unfertig',
          bereich: kennung,
          beschriftung: kennung.charAt(0).toUpperCase() + kennung.slice(1),
        },
      );
    },
    [setzeMaske],
  );

  /**
   * In eine GRUPPE wechseln (12.09.2026).
   *
   * EIN EINZIGER SICHTBARER PUNKT FUEHRT DIREKT IN SEINE MASKE. „Uebersicht"
   * fuehrt auf das Dashboard, „Faktura" auf die Rechnungen — eine Seite mit einer
   * einzigen Karte ist ein Klick, der nichts erklaert.
   *
   * Gezaehlt werden die SICHTBAREN Punkte und nicht die vorhandenen: Wer von den
   * drei Stammdatenmasken nur die Produkte sehen darf, kommt dort ebenfalls
   * direkt hin.
   */
  const wechsleGruppe = useCallback(
    (kennung: string): void => {
      const gruppe = sichtbareGruppen(rechte, benutzer.kontoart).find((g) => g.kennung === kennung);
      if (gruppe === undefined) return;

      const einziger = gruppe.bereiche.length === 1 ? gruppe.bereiche[0] : undefined;
      if (einziger !== undefined) {
        wechsle(einziger.kennung);
        return;
      }

      setzeMaske({ art: 'bereichsseite', gruppe: gruppe.kennung });
    },
    [rechte, wechsle, setzeMaske],
  );

  /**
   * Der Kopf haengt an der Maske; die Akte meldet ihn selbst, sobald sie
   * geladen ist. Bis dahin — oder wenn sie nicht laedt — traegt sie einen
   * schlichten Kopf: Sonst stuende der Titel der vorigen Maske ueber ihr.
   *
   * Der Auslöser ist der PFAD, nicht das Objekt: Wird dieselbe Akte erneut
   * gewaehlt (Suche, Rueckweg), bleibt die Maske stehen und laedt nicht neu —
   * ein neuer Platzhalter ueberschriebe dann den gemeldeten Kopf fuer immer.
   */
  const maskeJetzt = useRef(maske);
  maskeJetzt.current = maske;
  /* Auftrag #36: die Seite und nicht die Adresse — ein Reiterwechsel ist kein Maskenwechsel. */
  const maskenpfad = schluesselVon(maske);
  useEffect(() => {
    const maske = maskeJetzt.current;
    switch (maske.art) {
      case 'arbeitsplatz':
        /*
         * DER TITEL IST DIE MASKE UND NICHT DER MENSCH.
         *
         * Hier stand "Guten Tag, <Name>". Das war aus zwei Gruenden falsch:
         *
         *   1. Die Ueberschrift bekommt bei jedem Maskenwechsel den Fokus
         *      (WCAG 2.4.3, siehe Rahmen). Was ein Vorleseprogramm dann
         *      ansagt und was der Fokusrahmen umschliesst, war der NAME des
         *      Benutzers — eine Auskunft, die er selbst am wenigsten braucht.
         *   2. Der eigene Name ist keine Einordnung. Auf jeder anderen Maske
         *      sagt der Titel, WO man ist; auf dieser sagte er, WER man ist,
         *      und das steht ohnehin rechts in der Kopfzeile.
         *
         * Der Kicker traegt das DATUM (2.10). Es ist die einzige Auskunft,
         * die auf dem Arbeitsplatz jeden Tag eine andere ist — und es steht
         * dort, wo auf jeder anderen Maske die Einordnung steht.
         */
        setzeKopf({
          kicker: new Intl.DateTimeFormat('de-DE', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }).format(new Date()),
          titel: 'Dashboard',
          unterzeile: 'Was heute ansteht — Kennzahlen, Fristen und offene Entscheidungen',
          kennzeichen: undefined,
        });
        break;
      /*
       * JEDE MASKE TRAEGT UNTER DEM TITEL EINE KURZE ZEILE (Auftraggeber,
       * 18.09.2026: „im header setze eine kurze info unter die überschrift").
       * Sie sagt in einem Halbsatz, was man hier tut — der Titel sagt, wo man
       * ist. Die Akten setzen ihre eigene: Kunde, Betreuer, Laufzeit.
       */
      case 'kundenliste':
        setzeKopf({
          titel: 'Kunden',
          unterzeile:
            maske.einstieg === 'service'
              ? 'Privatkunden suchen, öffnen und anlegen'
              : 'Firmen und öffentlichen Dienst suchen, öffnen und anlegen',
          kennzeichen: undefined,
        });
        break;
      case 'kundeAnlegen':
        setzeKopf({
          titel: 'Neuer Kunde',
          unterzeile: 'Stammdaten erfassen — die Kundennummer vergibt das System',
          kennzeichen: undefined,
        });
        break;
      case 'unfertig':
        setzeKopf({
          titel: maske.beschriftung,
          unterzeile: 'Dieser Bereich ist noch nicht gebaut',
          kennzeichen: undefined,
        });
        break;
      /*
       * DER TITEL IST DIE GRUPPE, UND DER KICKER SAGT, WAS DIE SEITE IST.
       *
       * „Vertrieb" allein liesse offen, ob man in einer Maske steht oder vor
       * einer Auswahl. Der Kicker traegt auf jeder anderen Maske die Einordnung
       * — hier ist sie „Bereiche".
       */
      case 'bereichsseite': {
        const gefunden = BEREICHSGRUPPEN.find((g) => g.kennung === maske.gruppe);
        setzeKopf({
          kicker: 'Bereiche',
          titel: gefunden?.beschriftung ?? 'Bereiche',
          ...(gefunden === undefined ? {} : { unterzeile: gefunden.erklaerung }),
          kennzeichen: undefined,
        });
        break;
      }
      case 'artikelliste':
        setzeKopf({
          titel: 'Produkte',
          unterzeile: 'Trainings, Events und Artikel mit Preis und Status',
          kennzeichen: undefined,
        });
        break;
      case 'warengruppen':
        setzeKopf({
          titel: 'Warengruppen',
          unterzeile: 'Gruppen vererben Steuerschlüssel und Erlöskonten an ihre Artikel',
          kennzeichen: undefined,
        });
        break;
      case 'preislisten':
        setzeKopf({
          titel: 'Preislisten',
          unterzeile: 'Standard, Mitglied und weitere Listen — mit Rangstufe und Gültigkeit',
          kennzeichen: undefined,
        });
        break;
      case 'rabattkonditionen':
        setzeKopf({
          titel: 'Rabattkonditionen',
          unterzeile: 'Rabatte je Kunde oder Kundengruppe — datiert und begrenzt',
          kennzeichen: undefined,
        });
        break;
      case 'artikelAnlegen':
        setzeKopf({
          titel: 'Neuer Artikel',
          unterzeile: 'Bezeichnung, Produktart und Vertriebsform — alles Weitere in der Akte',
          kennzeichen: undefined,
        });
        break;
      case 'angebotsliste':
        setzeKopf({
          titel: 'Angebote',
          unterzeile: 'Angebote schreiben, versenden und nachfassen',
          kennzeichen: undefined,
        });
        break;
      case 'angebotAnlegen':
        setzeKopf({
          titel: 'Neues Angebot',
          unterzeile: 'Kunde wählen, Positionen erfassen, dann versenden',
          kennzeichen: undefined,
        });
        break;
      case 'auftragsliste':
        setzeKopf({
          titel: 'Aufträge',
          unterzeile: 'Bestätigte Aufträge mit dem Stand der Fakturierung',
          kennzeichen: undefined,
        });
        break;
      case 'auftragAnlegen':
        setzeKopf({
          titel: 'Neuer Auftrag',
          unterzeile: 'Direkt beauftragt, ohne vorheriges Angebot',
          kennzeichen: undefined,
        });
        break;
      case 'auftragsakte':
        // Wie die uebrigen Akten: Sie kennt ihren Titel erst, wenn sie geladen ist.
        setzeKopf({ titel: 'Auftrag', kennzeichen: undefined });
        break;
      case 'fakturierungsliste':
        setzeKopf({
          titel: 'Fakturierungsliste',
          unterzeile: 'Erbrachte Leistungen, die noch nicht berechnet sind',
          kennzeichen: undefined,
        });
        break;
      case 'rahmenliste':
        setzeKopf({
          titel: 'Rahmenaufträge',
          unterzeile: 'Kontingente mit Laufzeit — verbraucht und offen',
          kennzeichen: undefined,
        });
        break;
      case 'rahmenAnlegen':
        setzeKopf({
          titel: 'Neuer Rahmenauftrag',
          unterzeile: 'Kunde, Laufzeit und Kontingent festlegen',
          kennzeichen: undefined,
        });
        break;
      case 'rahmenakte':
        setzeKopf({ titel: 'Rahmenauftrag', kennzeichen: undefined });
        break;
      case 'belegvorlagen':
        setzeKopf({
          titel: 'Belegvorlagen',
          unterzeile: 'Texte und Platzhalter der Belege — in Fassungen, mit Vorschau',
          kennzeichen: undefined,
        });
        break;
      case 'postfaecher':
        setzeKopf({
          titel: 'Postfächer',
          unterzeile: 'Absender und Zuordnung der ausgehenden Nachrichten',
          kennzeichen: undefined,
        });
        break;
      case 'checkingeraete':
        setzeKopf({
          titel: 'Check-in-Tablets',
          unterzeile: 'Die Tablets für den Selbst-Check-in am Veranstaltungsort',
          kennzeichen: undefined,
        });
        break;
      case 'portalzugaenge':
        setzeKopf({
          titel: 'Buchungsschnittstelle',
          unterzeile: 'Zugänge des Buchungsportals zur Schnittstelle',
          kennzeichen: undefined,
        });
        break;
      case 'navision':
        setzeKopf({
          titel: 'Navision',
          unterzeile: 'Übergabe an die Finanzbuchhaltung — Endpunkte und Warteschlange',
          kennzeichen: undefined,
        });
        break;
      case 'berichtsversand':
        setzeKopf({
          titel: 'Berichtsversand',
          unterzeile: 'Abos nach Zeitplan und ihre letzten Läufe — Gescheitertes zuerst sichtbar',
          kennzeichen: undefined,
        });
        break;
      case 'ki':
        setzeKopf({
          titel: 'KI',
          unterzeile: 'Modellzugang, Schalter, Qualität und Kosten — und warum die KI gerade ruht',
          kennzeichen: undefined,
        });
        break;
      case 'erechnung':
        setzeKopf({
          titel: 'E-Rechnung',
          unterzeile: 'Bereitschaft, Steuerschlüssel und Bankverbindung für die E-Rechnung',
          kennzeichen: undefined,
        });
        break;
      case 'geschaeftsdaten':
        setzeKopf({
          titel: 'Geschäftsdaten',
          unterzeile: 'Firma, Steuer, Register, Kontakt und Bank — so stehen sie auf den Belegen',
          kennzeichen: undefined,
        });
        break;
      case 'systemzustand':
        setzeKopf({
          titel: 'Systemzustand',
          unterzeile: 'Ob die Umsysteme antworten und was sich in den Warteschlangen staut',
          kennzeichen: undefined,
        });
        break;
      case 'buchungsvorgang':
        setzeKopf({
          titel: 'Buchungen',
          unterzeile: 'Buchung finden: umbuchen, stornieren, Teilnehmer und Zusatzleistungen',
          kennzeichen: undefined,
        });
        break;
      case 'buchungAnlegen':
        setzeKopf({
          titel: 'Neue Buchung',
          unterzeile: 'Kunde, Produkt und Termin wählen, dann buchen',
          kennzeichen: undefined,
        });
        break;
      case 'optionsanfragen':
        setzeKopf({
          titel: 'Anfragen',
          unterzeile: 'Terminwünsche aus Angeboten — offen und überfällig zuerst',
          kennzeichen: undefined,
        });
        break;
      case 'arbeitsliste':
        setzeKopf({
          titel: 'Arbeitsliste',
          unterzeile: 'Buchungen, bei denen Teilnehmernamen fehlen — nach Meldefrist',
          kennzeichen: undefined,
        });
        break;
      case 'widerrufe':
        /* LEG-17 */
        setzeKopf({
          titel: 'Widerrufe',
          unterzeile: 'Eingegangene Widerrufe — bestätigen, anerkennen oder ablehnen',
          kennzeichen: undefined,
        });
        break;
      case 'wiedervorlagen':
        /* KUN-15 */
        setzeKopf({
          titel: 'Meine Wiedervorlagen',
          unterzeile: 'Was Ihnen oder Ihrer Rolle wieder vorgelegt wird — fällig, überfällig, erledigt',
          kennzeichen: undefined,
        });
        break;
      case 'rezensionen':
        /* SUP-53 */
        setzeKopf({
          titel: 'Rezensionen',
          unterzeile: 'Google-Rezensionen der Standorte lesen und beantworten',
          kennzeichen: undefined,
        });
        break;
      case 'posteingang':
        /* SUP-03 */
        setzeKopf({
          titel: 'Postfächer',
          unterzeile: 'Die Mails an Info und Vertrieb als Vorgänge — neueste zuerst',
          kennzeichen: undefined,
        });
        break;
      case 'absagen':
        setzeKopf({
          titel: 'Absagen',
          unterzeile: 'Buchungen abgesagter Termine — umbuchen oder erstatten, bis keine offen ist',
          kennzeichen: undefined,
        });
        break;
      case 'assets':
        setzeKopf({
          titel: 'Assets',
          unterzeile: 'Fahrmodule, Räume, Fahrzeuge und Betriebsmittel',
          kennzeichen: undefined,
        });
        break;
      case 'trainerstamm':
        setzeKopf({
          titel: 'Trainer',
          unterzeile: 'Intern und extern — Qualifikation, Vertrag und Honorar',
          kennzeichen: undefined,
        });
        break;
      case 'besetzung':
        setzeKopf({
          titel: 'Besetzung',
          unterzeile: 'Termine, deren externe Besetzung noch aussteht — mit ihrer Frist',
          kennzeichen: undefined,
        });
        break;
      case 'bausteine':
        setzeKopf({
          titel: 'Bausteine',
          unterzeile: 'Aus welchen Bausteinen ein Training besteht und wie lange sie dauern',
          kennzeichen: undefined,
        });
        break;
      case 'honorare':
        setzeKopf({
          titel: 'Honorare',
          unterzeile: 'Honorartabelle je Leistungsart — netto, datiert, zweistufig', // RES-68
          kennzeichen: undefined,
        });
        break;
      case 'trainingsplanung':
        setzeKopf({
          titel: 'Trainingsplanung',
          unterzeile: 'Der ganze Betriebstag — alle Trainings mit ihren Anfangszeiten',
          kennzeichen: undefined,
        });
        break;
      case 'gebuehrenstaffeln':
        setzeKopf({
          titel: 'Storno- und Umbuchungsstaffeln',
          unterzeile: 'Was eine Absage oder Umbuchung je nach Frist kostet',
          kennzeichen: undefined,
        });
        break;
      case 'rechtstexte':
        setzeKopf({
          titel: 'Rechtstexte',
          unterzeile: 'AGB, Widerruf und Datenschutz — in Fassungen, mit Zustimmung',
          kennzeichen: undefined,
        });
        break;
      case 'textbausteine':
        setzeKopf({
          titel: 'Textbausteine',
          unterzeile: 'Wiederkehrende Antworten im Posteingang, mit Platzhaltern',
          kennzeichen: undefined,
        });
        break;
      /* Die Berichtsmasken melden ihren Kopf selbst, sobald sie geladen sind. */
      case 'berichtsliste':
        setzeKopf({
          titel: 'Berichte',
          unterzeile: 'Eigene Auswertungen aus dem Kennzahlenkatalog',
          kennzeichen: undefined,
        });
        break;
      case 'berichtNeu':
        setzeKopf({
          kicker: 'Bericht · Entwurf',
          titel: 'Neuer Bericht',
          unterzeile: 'Kennzahl wählen, gruppieren, filtern',
          kennzeichen: undefined,
        });
        break;
      case 'berichtsakte':
        setzeKopf({
          kicker: 'Bericht',
          titel: 'Bericht',
          unterzeile: 'Wird geladen',
          kennzeichen: undefined,
        });
        break;
      case 'betriebspruefung':
        setzeKopf({
          titel: 'Betriebsprüfung',
          unterzeile: 'Festschreibung, Kontrollsystem, Konfiguration und Berechtigungen',
          kennzeichen: undefined,
        });
        break;
      case 'datenschutz':
        setzeKopf({
          titel: 'Datenschutz',
          unterzeile: 'Auskunft, Einschränkung und Werbewiderspruch je Person',
          kennzeichen: undefined,
        });
        break;
      case 'standorte':
        setzeKopf({
          titel: 'Standorte',
          unterzeile: 'Bezeichnung, Anschrift und Bundesland je Standort — die Kennung bleibt',
          kennzeichen: undefined,
        });
        break;
      case 'kalender':
        /* FRS-08 */
        setzeKopf({
          titel: 'Feiertage und Schließtage',
          unterzeile: 'Gesetzliche Feiertage je Land, Schließtage je Standort',
          kennzeichen: undefined,
        });
        break;
      case 'einstellungen':
        setzeKopf({
          titel: 'Einstellungen',
          unterzeile: 'Planungsregeln, Fristen und Vorläufe',
          kennzeichen: undefined,
        });
        break;
      case 'uebernahme':
        setzeKopf({
          titel: 'Datenübernahme',
          unterzeile: 'Bestand aus Dateien einlesen — mit Probelauf und Gedächtnis',
          kennzeichen: undefined,
        });
        break;
      /*
       * DER EMPFANG HATTE KEINEN KOPF und behielt den Titel der Maske davor —
       * wer von den Kunden kam, stand unter „Kunden" am Tresen.
       */
      case 'empfang':
        setzeKopf({
          titel: 'Empfang',
          unterzeile: 'Die Plätze des Tages je Termin — Anwesenheit abhaken, fehlende Namen klären',
          kennzeichen: undefined,
        });
        break;
      case 'artikelakte':
        // Die Akte meldet ihren Kopf selbst, sobald sie geladen ist.
        setzeKopf({ titel: 'Artikel', kennzeichen: undefined });
        break;
      case 'termine':
        setzeKopf({
          titel: 'Termine',
          unterzeile: 'Alle Termine mit Status, Auslastung und Veröffentlichung',
          kennzeichen: undefined,
        });
        break;
      case 'terminakte':
        // Wie die übrigen Akten: Sie kennt ihren Titel erst, wenn sie geladen ist.
        setzeKopf({ titel: 'Termin', kennzeichen: undefined });
        break;
      case 'meinkonto':
        setzeKopf({
          titel: 'Mein Konto',
          unterzeile: 'Anmeldung, Geräte und Sitzungen',
          kennzeichen: undefined,
        });
        break;
      case 'benutzerliste':
        setzeKopf({
          titel: 'Benutzer und Rollen',
          unterzeile: 'Wer sich anmelden darf und was er sehen und ändern kann',
          kennzeichen: undefined,
        });
        break;
      case 'benutzerakte':
        // Wie die Kundenakte: Sie kennt ihren Titel erst, wenn sie geladen ist.
        setzeKopf({
          titel: 'Benutzer',
          unterzeile: 'Konto, Rollen, Vertretungen und Anmeldung',
          kennzeichen: undefined,
        });
        break;
      case 'bestaetigungen':
        setzeKopf({
          titel: 'Vier-Augen-Vorgänge',
          unterzeile: 'Vorgänge, die eine zweite Person freigeben muss',
          kennzeichen: undefined,
        });
        break;
      case 'rechnungsliste':
        setzeKopf({
          titel: 'Rechnungen',
          unterzeile: 'Rechnungen erzeugen, prüfen und festschreiben',
          kennzeichen: undefined,
        });
        break;
      case 'rechnungAnlegen':
        setzeKopf({
          titel: 'Neue Rechnung',
          unterzeile: 'Ohne Angebot: zuerst der Auftrag, dann die Rechnung',
          kennzeichen: undefined,
        });
        break;
      case 'offeneposten':
        setzeKopf({
          titel: 'Offene Posten',
          unterzeile: 'Unbezahlte Rechnungen nach Fälligkeit, mit dem Mahnstand aus Navision',
          kennzeichen: undefined,
        });
        break;
      case 'bgAbrechnung':
        setzeKopf({
          // PRD-38 (CRM-163): allgemein Kostenträger, nicht nur die BG.
          titel: 'Abrechnung an Kostenträger',
          unterzeile: 'Angerechnete Gutscheine je Kostenträger abrechnen, ohne erneute Umsatzsteuer',
          kennzeichen: undefined,
        });
        break;
      case 'rechnungsakte':
        setzeKopf({ titel: 'Rechnung', kennzeichen: undefined });
        break;
      case 'angebotsakte':
        // Wie die Kundenakte: Sie kennt ihren Titel erst, wenn sie geladen ist.
        setzeKopf({ titel: 'Angebot', kennzeichen: undefined });
        break;
      case 'webanfragen':
        setzeKopf({
          titel: 'Webanfragen',
          unterzeile: 'Anfragen aus den Formularen des Auftritts zuordnen, erledigen, verwerfen',
          kennzeichen: undefined,
        });
        break;
      case 'kontozuordnungen':
        setzeKopf({
          titel: 'Kontozuordnungen',
          unterzeile: 'Neue Online-Konten einem Kunden zuordnen — und Zuordnungen zurücknehmen',
          kennzeichen: undefined,
        });
        break;
      case 'zusammenfuehren':
        setzeKopf({
          titel: 'Dubletten zusammenführen',
          unterzeile: 'Zwei Kundensätze zu einem — mit Vorschau, was übernommen wird',
          kennzeichen: undefined,
        });
        break;
      case 'kundenakte':
        // Die Akte kennt ihren Titel erst, wenn sie geladen ist.
        setzeKopf({ titel: 'Kunde', kennzeichen: undefined });
        break;
    }
  }, [maskenpfad, benutzer.name]);

  const darf = (objekt: string, aktion: string): boolean =>
    rechte.some((r) => r.objekt === objekt && r.aktion === aktion);
  /*
   * BUC-75, M-61 (CRM-152): „Neue Rechnung" legt einen Auftrag an, bestätigt ihn
   * und rechnet ab — sie verlangt die Rechte des langen Wegs, nicht weniger.
   */
  const darfDirektabrechnen =
    darf('rechnung', 'anlegen') &&
    darf('auftrag', 'anlegen') &&
    darf('auftrag', 'aendern') &&
    darf('auftrag', 'bestaetigen');

  /*
   * CRM-118 (L7; AUF-06, BUC-50): die Wege zwischen den Akten, je Ziel an das
   * Sehrecht seiner Maske gebunden. Ohne Recht fehlt der Weg, und der Verweis
   * bleibt Text (BEN-02).
   */
  const aktenwege: Aktenwege = {
    ...(darf('kunde', 'sehen') ? { kunde: (id: string) => setzeMaske({ art: 'kundenakte', id }) } : {}),
    ...(darf('angebot', 'sehen')
      ? { angebot: (id: string) => setzeMaske({ art: 'angebotsakte', id }) }
      : {}),
    ...(darf('auftrag', 'sehen')
      ? {
          auftrag: (id: string) => setzeMaske({ art: 'auftragsakte', id }),
          rahmen: (id: string) => setzeMaske({ art: 'rahmenakte', id }),
        }
      : {}),
    ...(darf('rechnung', 'sehen')
      ? { rechnung: (id: string) => setzeMaske({ art: 'rechnungsakte', id }) }
      : {}),
    ...(darf('termin', 'sehen') ? { termin: (id: string) => setzeMaske({ art: 'terminakte', id }) } : {}),
    ...(darf('buchung', 'sehen')
      ? { buchung: (nummer: string) => setzeMaske({ art: 'buchungsvorgang', nummer }) }
      : {}),
  };

  /**
   * Die Reiter gehoeren IN DEN SEITENKOPF (2.10, Skizzen zu M-12 und M-31), nicht
   * als erstes Element in den Rumpf. Sie stehen deshalb hier, wo der Rahmen
   * sie entgegennehmen kann — und einmal als Daten statt sechsmal als Markup.
   *
   * Bis hierher trugen Fakturierungsliste, Rahmenauftraege und Offene Posten
   * statt der Reiterleiste einen Knopf "Zu den Auftraegen". Das war ein
   * Rueckweg, kein Reiter: Wer von der Fakturierungsliste zu den
   * Rahmenauftraegen wollte, ging ueber zwei Masken. Es sind drei Sichten auf
   * denselben Bestand, und drei Sichten tragen drei Reiter.
   */
  const REITERGRUPPEN: readonly {
    readonly beschriftung: string;
    readonly sichten: readonly {
      readonly maske: Maske;
      readonly text: string;
      readonly auch?: readonly Maske['art'][];
      /**
       * Das Recht dieses REITERS, wenn es enger ist als das des Menuepunkts
       * (B4, 12.09.2026).
       *
       * Bis zum Review trug nur der Menuepunkt ein Recht, und die Reiter dahinter
       * standen fuer jeden da, der hereinkam: Operations sah „Benutzer und
       * Rollen" und bekam beim Anfassen eine Abweisung. Ein Reiter, den man
       * nicht oeffnen kann, ist ein Reiter zu viel — FRS-15 sagt es fuer
       * Handlungen, und fuer Wege gilt es genauso.
       *
       * Fehlt die Angabe, gilt das Recht des Menuepunkts.
       */
      readonly verlangt?: { readonly objekt: string; readonly aktionen: readonly string[] };
    }[];
  }[] = [
    {
      beschriftung: 'Auftragssichten',
      sichten: [
        { maske: { art: 'auftragsliste' }, text: 'Aufträge' },
        { maske: { art: 'fakturierungsliste' }, text: 'Fakturierungsliste' },
        { maske: { art: 'rahmenliste' }, text: 'Rahmenaufträge' },
      ],
    },
    {
      /*
        DREI SICHTEN AUF DENSELBEN BESTAND (SYS-10).

        Produkte, ihre Warengruppen und ihre Preislisten gehoeren zusammen:
        Die Gruppe vererbt Steuerschluessel und Erloeskonto an den Artikel
        (PRD-02, PRD-04), die Preisliste traegt seinen Preis (PRD-13). Sie
        als eigene Menuepunkte zu fuehren zerrisse, was man beim Pflegen
        nacheinander braucht.
      */
      beschriftung: 'Produktsichten',
      sichten: [
        {
          maske: { art: 'artikelliste' },
          text: 'Produkte',
          auch: ['artikelakte', 'artikelAnlegen'],
        },
        { maske: { art: 'warengruppen' }, text: 'Warengruppen' },
        { maske: { art: 'preislisten' }, text: 'Preislisten' },
        { maske: { art: 'rabattkonditionen' }, text: 'Rabattkonditionen' },
      ],
    },
    {
      beschriftung: 'Rechnungssichten',
      sichten: [
        { maske: { art: 'rechnungsliste' }, text: 'Rechnungen' },
        { maske: { art: 'offeneposten' }, text: 'Offene Posten' },
        /* PRD-38 (CRM-157, CRM-163): Die Abrechnung an den Kostenträger macht die Faktura. */
        {
          maske: { art: 'bgAbrechnung' },
          text: 'Abrechnung an Kostenträger',
          verlangt: { objekt: 'bgzuschuss', aktionen: ['abrechnen'] },
        },
      ],
    },
    /*
      ============================================================================
      AUS ZWOELF REITERN WURDEN VIER GRUPPEN (B4, 12.09.2026)
      ============================================================================

      Der Reiter traegt drei bis fuenf Sichten AUF DENSELBEN BESTAND — so wie es
      Auftraege, Produkte und Rechnungen daneben tun. Zwoelf Reiter in einer Zeile
      waren keine Gliederung mehr, sondern ein zweites Menue, und sie mischten
      Belegwesen, Umsysteme, Hausdaten und Personen.

      Jede Gruppe hat jetzt einen eigenen Menuepunkt mit einem Recht, das ihre
      Zustaendigkeit trifft, und jeder Reiter darf ein engeres tragen. Erfunden
      wurde kein Recht (BEN-02).
    */
    {
      beschriftung: 'Verwaltung',
      sichten: [
        { maske: { art: 'geschaeftsdaten' }, text: 'Geschäftsdaten' },
        /* RES-01, TAG-01 (Auftrag #12): vor dem Kalender, der ihre Schließtage führt. */
        { maske: { art: 'standorte' }, text: 'Standorte' },
        { maske: { art: 'einstellungen' }, text: 'Einstellungen' },
        { maske: { art: 'kalender' }, text: 'Feiertage und Schließtage' },
        {
          maske: { art: 'uebernahme' },
          text: 'Datenübernahme',
          verlangt: { objekt: 'einstellungen', aktionen: ['konfigurieren'] },
        },
      ],
    },
    {
      beschriftung: 'Belege und Vorlagen',
      sichten: [
        /*
          Die Belegvorlage ist enger als die Gruppe: Ihre Pflege haengt an
          `einstellungen/vorlagen_pflegen` und traegt damit nur die
          Administration. Staffeln und Rechtstexte darf auch lesen, wer am Telefon
          eine Gebuehr nennt — sie stammen aus den eigenen
          Geschaeftsbedingungen (BUC-45).
        */
        {
          maske: { art: 'belegvorlagen' },
          text: 'Belegvorlagen',
          verlangt: { objekt: 'einstellungen', aktionen: ['vorlagen_pflegen'] },
        },
        {
          maske: { art: 'gebuehrenstaffeln' },
          text: 'Storno-Staffeln',
          verlangt: { objekt: 'gebuehrenstaffel', aktionen: ['sehen'] },
        },
        {
          maske: { art: 'rechtstexte' },
          text: 'Rechtstexte',
          verlangt: { objekt: 'rechtstext', aktionen: ['sehen'] },
        },
        /* SUP-20 (Auftrag CRM-178): lesen mit support/sehen, pflegen mit support/vorlagen. */
        {
          maske: { art: 'textbausteine' },
          text: 'Textbausteine',
          verlangt: { objekt: 'support', aktionen: ['sehen'] },
        },
        /*
         * AUS-15 (Auftrag #30): die Übersicht aller Abos des Berichtsversands für
         * die Administration — was das Haus an wen verschickt, neben Vorlagen und
         * Rechtstexten, die ebenfalls hinausgehen.
         */
        {
          maske: { art: 'berichtsversand' },
          text: 'Berichtsversand',
          verlangt: { objekt: 'einstellungen', aktionen: ['sehen'] },
        },
      ],
    },
    {
      beschriftung: 'Umsysteme',
      sichten: [
        { maske: { art: 'postfaecher' }, text: 'Postfächer' },
        { maske: { art: 'navision' }, text: 'Navision' },
        { maske: { art: 'erechnung' }, text: 'E-Rechnung' },
        { maske: { art: 'ki' }, text: 'KI' },
        {
          maske: { art: 'portalzugaenge' },
          text: 'Buchungsschnittstelle',
          verlangt: { objekt: 'umsystem', aktionen: ['sehen'] },
        },
      ],
    },
    {
      beschriftung: 'Benutzer und Recht',
      sichten: [
        { maske: { art: 'benutzerliste' }, text: 'Benutzer und Rollen', auch: ['benutzerakte'] },
        { maske: { art: 'bestaetigungen' }, text: 'Vier-Augen-Vorgänge' },
        /*
          TAG-49 (Auftrag #56, 02.10.2026): Das Tablet ist kein Zugang nach
          aussen, sondern ein Geraet im Haus, das sich anmeldet. Unter den
          Umsystemen war es seit #53 der sechste Reiter.

          Das Recht bleibt am Reiter, obwohl der Menuepunkt dasselbe verlangt:
          Das Band steht vor jeder Maske der Gruppe, auch vor einer, die man
          ueber ihre Adresse geoeffnet hat — `/bestaetigungen` leuchtet unter
          den Terminen. So sieht die Tablets, wer sie bisher sah.
        */
        {
          maske: { art: 'checkingeraete' },
          text: 'Check-in-Tablets',
          verlangt: { objekt: 'einstellungen', aktionen: ['sehen'] },
        },
      ],
    },
  ];

  const gruppe = REITERGRUPPEN.find((g) =>
    g.sichten.some((s) => s.maske.art === maske.art || (s.auch ?? []).includes(maske.art)),
  );

  /*
   * NUR DIE REITER, DIE SICH AUCH OEFFNEN LASSEN (B4, 12.09.2026).
   *
   * Ein Reiter ohne Recht dahinter fuehrt in eine Abweisung. Die Rechtepruefung
   * bleibt serverseitig (BEN-08) — das hier blendet nur aus, was ohnehin nicht
   * geht.
   */
  const sichtbareSichten =
    gruppe === undefined
      ? []
      : gruppe.sichten.filter(
          (s) =>
            s.verlangt === undefined
            || rechte.some(
              (r) => r.objekt === s.verlangt?.objekt && s.verlangt.aktionen.includes(r.aktion),
            ),
        );

  /*
   * EIN EINZELNER REITER IST KEINE WAHL.
   *
   * Bleibt nach dem Filtern nur eine Sicht uebrig, steht das Band nicht da: Eine
   * Reiterleiste mit einem Reiter sieht wie eine Auswahl aus, die es nicht gibt.
   */
  const reiter =
    gruppe === undefined || sichtbareSichten.length < 2 ? undefined : (
      <div className="reiter" role="tablist" aria-label={gruppe.beschriftung}>
        {sichtbareSichten.map((sicht) => {
          const hier = sicht.maske.art === maske.art || (sicht.auch ?? []).includes(maske.art);
          return (
            <button
              key={sicht.maske.art}
              type="button"
              role="tab"
              aria-selected={hier}
              className={`reiter__wahl${hier ? ' reiter__wahl--hier' : ''}`}
              onClick={() => setzeMaske(sicht.maske)}
            >
              {sicht.text}
            </button>
          );
        })}
      </div>
    );

  /**
   * M-07: der persoenliche Verlauf.
   *
   * Vermerkt wird HIER und nicht im Suchfenster. Ein Verlauf, der nur
   * enthaelt, was ueber das Fenster geoeffnet wurde, beantwortet die Frage
   * nicht, fuer die er da ist — "Wo war ich gerade?" gilt auch fuer den Weg
   * ueber die Liste oder den Verweis in einer Akte.
   */
  useEffect(() => {
    const zuArt: Partial<Record<Maske['art'], Suchart>> = {
      kundenakte: 'kunde',
      angebotsakte: 'angebot',
      auftragsakte: 'auftrag',
      rechnungsakte: 'rechnung',
      terminakte: 'termin',
    };
    const art = zuArt[maske.art];
    if (art === undefined || !('id' in maske)) return;
    // Scheitert es, bleibt es folgenlos: Der Verlauf ist eine Bequemlichkeit,
    // und eine geoeffnete Akte darf daran nicht haengen.
    void vermerken(art, maske.id).catch(() => undefined);
  }, [maske]);

  /**
   * Ein Treffer aus dem Suchfenster fuehrt in seine Maske.
   *
   * Der Ansprechpartner hat keine eigene: Er steht in der Kundenakte. Das
   * Fenster bleibt dann offen, statt still nichts zu tun. Ein Termin öffnet
   * seine Akte (SYS-08, CRM-117).
   */
  const zurWahl = (art: Suchart, id: string): boolean => {
    switch (art) {
      case 'kunde':
        setzeMaske({ art: 'kundenakte', id });
        return true;
      case 'angebot':
        setzeMaske({ art: 'angebotsakte', id });
        return true;
      case 'auftrag':
        setzeMaske({ art: 'auftragsakte', id });
        return true;
      case 'rechnung':
        setzeMaske({ art: 'rechnungsakte', id });
        return true;
      case 'termin':
        setzeMaske({ art: 'terminakte', id });
        return true;
      default:
        return false;
    }
  };

  /**
   * "Alle Treffer anzeigen" fuehrt in die Liste der gewaehlten Art.
   *
   * Das Pflichtenheft nennt dafuer die globale Trefferliste M-03. Die gibt es
   * noch nicht — und eine leere Maske mit ihrem Namen waere schlechter als
   * die Liste, die es gibt. Uebernommen wird, was die Liste kennt: bei den
   * Kunden der Suchbegriff, sonst nichts.
   */
  const zurListe = (art: Suchart, felder: Felder): void => {
    setzeSuchfenster(false);
    switch (art) {
      case 'kunde':
        setzeMaske({ art: 'kundenliste', ...(felder.text === undefined ? {} : { suche: felder.text }) });
        return;
      case 'angebot':
        setzeMaske({ art: 'angebotsliste' });
        return;
      case 'auftrag':
        setzeMaske({ art: 'auftragsliste' });
        return;
      case 'rechnung':
        setzeMaske({ art: 'rechnungsliste' });
        return;
      default:
        return;
    }
  };

  /**
   * „NEU" IN DER KOPFZEILE (ÄA-41): die Anlagemasken, die der Benutzer darf.
   *
   * Jede an dem Recht, an dem auch ihre Maske hängt — wer keinen Auftrag
   * anlegen darf, bekommt den Eintrag nicht angeboten, statt in einer Absage
   * zu landen (FRS-15). Der Kunde nimmt den Einstieg mit, aus dem man kommt.
   */
  const anlagen = [
    ...(darf('kunde', 'anlegen')
      ? [
          {
            text: 'Kunde',
            beiWahl: () => setzeMaske({ art: 'kundeAnlegen', ...mitEinstieg('einstieg' in maske ? maske : {}) }),
          },
        ]
      : []),
    ...(darf('angebot', 'anlegen')
      ? [{ text: 'Angebot', beiWahl: () => setzeMaske({ art: 'angebotAnlegen' }) }]
      : []),
    ...(darf('auftrag', 'anlegen')
      ? [
          { text: 'Auftrag', beiWahl: () => setzeMaske({ art: 'auftragAnlegen' }) },
          { text: 'Rahmenauftrag', beiWahl: () => setzeMaske({ art: 'rahmenAnlegen' }) },
        ]
      : []),
    ...(darfDirektabrechnen
      ? [{ text: 'Rechnung', beiWahl: () => setzeMaske({ art: 'rechnungAnlegen' }) }]
      : []),
    /* BUC-50: Die Buchung legt man in der Buchungsmaske an. */
    ...(darf('buchung', 'anlegen')
      ? [{ text: 'Buchung', beiWahl: () => setzeMaske({ art: 'buchungsvorgang' }) }]
      : []),
    ...(darf('artikel', 'anlegen')
      ? [{ text: 'Artikel', beiWahl: () => setzeMaske({ art: 'artikelAnlegen' }) }]
      : []),
  ];

  return (
    <Rahmen
      benutzer={benutzer}
      rechte={rechte}
      sinnbild={kopf.sinnbild}
      kennzahlen={kopf.kennzahlen}
      titelkarte={kopf.titelkarte}
      kompakt={kopf.kompakt}
      kopfhandlungen={kopf.handlungen ?? zusatz.handlungen}
      anlagen={anlagen}
      glocke={
        glocke === undefined
          ? undefined
          : {
              faellig: glocke.faellig,
              ueberfaellig: glocke.ueberfaellig > 0,
              eintraege: glockeneintraege(glocke, {
                zurWiedervorlage: (id) => setzeMaske({ art: 'wiedervorlagen', oeffnen: id }),
                zuMeinenWiedervorlagen: darf('kunde', 'sehen')
                  ? () => setzeMaske({ art: 'wiedervorlagen' })
                  : undefined,
                zumTermin: (id) => setzeMaske({ art: 'terminakte', id }),
                zumAngebot: (id) => setzeMaske({ art: 'angebotsakte', id }),
                // SUP-25 (Auftrag CRM-178): der Vorgang im Reiter seines Postfachs.
                zumVorgang: darf('support', 'sehen')
                  ? (vorgang, postfach) => setzeMaske({ art: 'posteingang', vorgang, postfach })
                  : undefined,
              }),
            }
      }
      /*
        DIE HILFE ZUR MASKE — AN EINER STELLE FÜR ALLE (13.09.2026, NFA-17).

        Auftraggeber: „Es soll eine Hilfsfunktion für jede Maske geben."

        Sie steht HIER und nicht in den Masken: Diese Stelle kennt `maske.art`,
        und damit bekommt jede Maske ihre Hilfe, ohne dass jemand sie einbaut —
        auch die nächste. Seit ÄA-46 (Auftrag CRM-51) klappt sie unter dem
        Symbol im Kopf auf; die Zeile unter dem Titel ist entfallen.

        Der Text steht in `maskenhilfe.ts`; welche Maske dort fehlt, sagt der
        Übersetzer.
      */
      hilfe={
        <Maskenhilfe
          maske={maske}
          titel={kopf.titel}
          rechte={rechte}
          kontoart={benutzer.kontoart}
          beiMaske={setzeMaske}
        />
      }
      bereich={stelle.bereich}
      gruppe={stelle.gruppe}
      beiBereich={wechsle}
      beiGruppe={wechsleGruppe}
      beiMeinKonto={() => setzeMaske({ art: 'meinkonto' })}
      titel={kopf.titel}
      /* Die Zeitachse des Tagesplans nimmt die volle Fensterbreite (TAG-03). */
      breit={maske.art === 'trainingsplanung'}
      kicker={kopf.kicker}
      unterzeile={kopf.unterzeile}
      kennzeichen={
        kopf.kennzeichen
        ?? (zusatz.zaehler === undefined ? undefined : (
          <span className="marke marke--akzent">{zusatz.zaehler}</span>
        ))
      }
      reiter={reiter}
      /*
        Die haeufigste Folgehandlung des Bereichs, rechts im Seitenkopf — an
        DERSELBEN Stelle wie bei den Kunden. Sie fehlte fuer die Produkte:
        Der einzige Weg zur Neuanlage fuehrte ueber eine erfolglose Suche,
        also ueber mindestens drei getippte Zeichen und eine Liste ohne
        Treffer.
      */
      handlung={
        maske.art === 'kundenliste' && darf('kunde', 'anlegen') ? (
          <>
            {/* WEB-02: die Anfragen aus dem Auftritt, neben der Neuanlage. */}
            <button
              type="button"
              className="knopf"
              onClick={() => setzeMaske({ art: 'webanfragen' })}
            >
              Webanfragen
            </button>
            {/* WEB-10 i.d.F. AeA-37: mehrdeutige Online-Konten. */}
            <button
              type="button"
              className="knopf"
              onClick={() => setzeMaske({ art: 'kontozuordnungen' })}
            >
              Kontozuordnungen
            </button>
            <button
              type="button"
              className="knopf knopf--haupt"
              onClick={() => setzeMaske({ art: 'kundeAnlegen', ...mitEinstieg(maske) })}
            >
              Neuer Kunde
            </button>
          </>
        ) : maske.art === 'artikelliste' && darf('artikel', 'anlegen') ? (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => setzeMaske({ art: 'artikelAnlegen' })}
          >
            Neuer Artikel
          </button>
        ) : maske.art === 'angebotsliste' && darf('angebot', 'anlegen') ? (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => setzeMaske({ art: 'angebotAnlegen' })}
          >
            Neues Angebot
          </button>
        ) : maske.art === 'auftragsliste' && darf('auftrag', 'anlegen') ? (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => setzeMaske({ art: 'auftragAnlegen' })}
          >
            Neuer Auftrag
          </button>
        ) : maske.art === 'rechnungsliste' && darfDirektabrechnen ? (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => setzeMaske({ art: 'rechnungAnlegen' })}
          >
            Neue Rechnung
          </button>
        ) : maske.art === 'berichtsliste' ? (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => setzeMaske({ art: 'berichtNeu' })}
          >
            Neuer Bericht
          </button>
        ) : maske.art === 'rahmenliste' && darf('auftrag', 'anlegen') ? (
          <button
            type="button"
            className="knopf knopf--haupt"
            onClick={() => setzeMaske({ art: 'rahmenAnlegen' })}
          >
            Neuer Rahmenauftrag
          </button>
        ) : undefined
      }
      beiSuchen={() => setzeSuchfenster(true)}
      beiAbmelden={beiAbmelden}
    >
      <SeitenkontextAnbieter value={meldeZusatz}>
      {/*
        ERR-11 IM RAHMEN (ÄA-41): Scheitert eine Maske beim Aufbau, bleibt der
        Rahmen stehen. Der Schlüssel ist die Adresse — der Wechsel in eine
        andere Maske fängt frisch an.
      */}
      <Auffangnetz eingebettet key={schluesselVon(maske)}>
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {suchfensterOffen && (
        <Suchfenster
          betriebsart="springen"
          arten={suchbareArten}
          beiSchliessen={() => setzeSuchfenster(false)}
          beiWahl={(wahl) => {
            if (zurWahl(wahl.art, wahl.id)) setzeSuchfenster(false);
          }}
          beiAlleTreffer={zurListe}
        />
      )}

      <AbTablet an={NUR_AB_TABLET.has(maske.art)}>
      {maske.art === 'arbeitsplatz' && (
        <Arbeitsplatz
          beiBereich={wechsle}
          beiVorgang={zurWahl}
          /* Auftrag #27: ein Bericht-Widget oeffnet seinen Bericht. */
          beiBericht={(id) => setzeMaske({ art: 'berichtsakte', id })}
          /* Auftrag #37: ein Fall aus der Liste eines Widgets — die Buchung über ihre Nummer. */
          beiAkte={(ziel) => {
            if (ziel.art === 'buchung') setzeMaske({ art: 'buchungsvorgang', nummer: ziel.nummer });
            else zurWahl(ziel.art, ziel.id);
          }}
        />
      )}

      {/*
        DIE SEITE EINER GRUPPE (12.09.2026).

        Sie bekommt die Gruppe AUS DER RECHTEFILTERUNG und nicht aus
        BEREICHSGRUPPEN: Was hier als Karte steht, muss dasselbe sein, was in der
        Spalte steht — sonst verspricht eine Karte eine Maske, die der Server
        abweist (4.3, BEN-08).

        Findet sie ihre Gruppe nicht, zeichnet sie nichts. Das kann nur passieren,
        wenn einem Benutzer mitten in der Sitzung ein Recht entzogen wird; dann
        ist eine leere Fläche die richtige Antwort und nicht eine Ausnahme.
      */}
      {maske.art === 'bereichsseite'
        && (() => {
          const gruppe = sichtbareGruppen(rechte, benutzer.kontoart).find((g) => g.kennung === maske.gruppe);
          return gruppe === undefined ? null : (
            <Bereichsseite gruppe={gruppe} bereich={stelle.bereich} beiBereich={wechsle} />
          );
        })()}

      {maske.art === 'kundenliste' && (
        <Kundenliste
          /* Ein anderer Einstieg ist eine andere Liste — mit ihrer eigenen
             Vorbelegung und nicht mit den Filtern der vorigen. */
          key={maske.einstieg ?? 'vertrieb'}
          anfangsSuche={maske.suche ?? ''}
          /*
            WER MIT EINEM SUCHBEGRIFF KOMMT, SUCHT IN ALLEN ARTEN. „Alle Treffer
            anzeigen" aus dem Suchfenster hat den Privatkunden schon gefunden;
            die Vorbelegung des Vertriebs nähme ihn wieder heraus.
          */
          vorbelegung={maske.suche === undefined ? VORBELEGUNG[maske.einstieg ?? 'vertrieb'] : []}
          beiOeffnen={(id) => setzeMaske({ art: 'kundenakte', id, ...mitEinstieg(maske) })}
          beiNeu={(name) => setzeMaske({ art: 'kundeAnlegen', name, ...mitEinstieg(maske) })}
        />
      )}

      {maske.art === 'warengruppen' && (
        <Warengruppen darfKonfigurieren={darf('artikel', 'konfigurieren')} />
      )}

      {maske.art === 'preislisten' && (
        <Preislisten darfKonfigurieren={darf('artikel', 'konfigurieren')} />
      )}

      {maske.art === 'rabattkonditionen' && (
        <Rabattkonditionen darfKonfigurieren={darf('artikel', 'konfigurieren')} />
      )}

      {maske.art === 'artikelliste' && (
        <Artikelliste
          beiOeffnen={(id) => setzeMaske({ art: 'artikelakte', id })}
          beiNeu={(bezeichnung) => setzeMaske({ art: 'artikelAnlegen', bezeichnung })}
        />
      )}

      {maske.art === 'artikelAnlegen' && (
        <ArtikelAnlegen
          anfangsBezeichnung={maske.bezeichnung ?? ''}
          beiAngelegt={(id) => setzeMaske({ art: 'artikelakte', id })}
          beiAbbruch={() => setzeMaske({ art: 'artikelliste' })}
        />
      )}

      {maske.art === 'artikelakte' && (
        <Artikelakte
          id={maske.id}
          // SYS-10: Preispflege haengt an `artikel/konfigurieren` und nicht an
          // `artikel/aendern`. Ohne dieses Recht bietet die Preistafel keine
          // Handlungen an, die dem Anwender ohnehin nie gelingen.
          darfKonfigurieren={darf('artikel', 'konfigurieren')}
          /*
            SYS-10, zweite Haelfte: Varianten, Teilnahmevoraussetzungen und
            Verpflegungsangaben beschreiben, WAS verkauft wird — sie haengen
            an `artikel/aendern`. Wer Preise pflegen darf, aendert damit noch
            kein Mindestalter.
          */
          darfAendern={darf('artikel', 'aendern')}
          darfWartelisteSehen={darf('buchung', 'sehen')}
          darfWartelisteFuehren={darf('buchung', 'anlegen')}
          beiKopf={setzeKopf}
          beiListe={() => setzeMaske({ art: 'artikelliste' })}
          beiOeffnen={(anderer) => setzeMaske({ art: 'artikelakte', id: anderer })}
        />
      )}

      {maske.art === 'angebotsliste' && (
        <Angebotsliste
          zurKunde={aktenwege.kunde}
          beiOeffnen={(id) => setzeMaske({ art: 'angebotsakte', id })}
          /* SYS-01, BEN-02 (Auftrag #32): der Anlageknopf nur mit `angebot:anlegen`. */
          beiNeu={darf('angebot', 'anlegen') ? () => setzeMaske({ art: 'angebotAnlegen' }) : undefined}
        />
      )}

      {maske.art === 'angebotAnlegen' && (
        <AngebotAnlegen
          {...(maske.kunde === undefined ? {} : { anfangsKunde: maske.kunde })}
          beiAngelegt={(id) => {
            const folge = maske.folge;
            if (folge === undefined) {
              setzeMaske({ art: 'angebotsakte', id });
              return;
            }
            /*
              SUP-23: Das Angebot aus einem Postfachvorgang meldet sich dort
              zurück. Scheitert das, bleibt die Folgeaktion angestoßen und lässt
              sich im Vorgang unter „Ergebnis zuordnen" nachholen.
            */
            void folgeaktionErgebnis(folge.folgeaktion, 'angebot', id)
              .catch(() => undefined)
              .finally(() => setzeMaske({ art: 'angebotsakte', id }));
          }}
          beiAbbruch={() =>
            setzeMaske(
              maske.folge === undefined
                ? { art: 'angebotsliste' }
                : { art: 'posteingang', vorgang: maske.folge.vorgang, postfach: maske.folge.postfach },
            )
          }
        />
      )}

      {maske.art === 'angebotsakte' && (
        <Angebotsakte
          id={maske.id}
          /*
            ZWEI RECHTE, DIE SICH AEHNELN UND ES NICHT SIND (Rollenmodell
            0006): `aendern` bearbeitet den Beleg, `versenden` gibt ihn
            hinaus — und sagt damit einen Preis zu. Wer Tippfehler
            berichtigen darf, bekommt deshalb keinen Versandknopf.
          */
          darfAendern={darf('angebot', 'aendern')}
          darfVersenden={darf('angebot', 'versenden')}
          darfLoeschen={darf('angebot', 'loeschen')}
          {...(darf('bgzuschuss', 'sehen')
            ? { bgVormerkungen: { darfVormerken: darf('bgzuschuss', 'erfassen') && darf('angebot', 'aendern') } }
            : {})}
          /*
            ANG-18: Die Umwandlung in einen Auftrag haengt an `auftrag/anlegen`
            und nicht an einem Angebotsrecht. Wer Angebote versenden darf,
            schliesst damit noch keinen Vertrag.
          */
          darfAuftragAnlegen={darf('auftrag', 'anlegen')}
          beiKopf={setzeKopf}
          beiListe={() => setzeMaske({ art: 'angebotsliste' })}
          beiVersion={(neu) => setzeMaske({ art: 'angebotsakte', id: neu })}
          beiAuftrag={(neu) => setzeMaske({ art: 'auftragsakte', id: neu })}
          wege={aktenwege}
          /* CRM-118: der Menüpunkt „Anfragen" hängt an `terminoption_freigeben` (ANG-38). */
          {...(darf('angebot', 'terminoption_freigeben')
            ? { beiOptionsanfragen: () => setzeMaske({ art: 'optionsanfragen' }) }
            : {})}
        />
      )}

      {maske.art === 'auftragsliste' && (
        <>
          <Auftragsliste
            zurKunde={aktenwege.kunde}
            beiOeffnen={(id) => setzeMaske({ art: 'auftragsakte', id })}
            beiNeu={() => setzeMaske({ art: 'auftragAnlegen' })}
          />
        </>
      )}

      {maske.art === 'fakturierungsliste' && (
        <>
          <Fakturierungsliste
            zurKunde={aktenwege.kunde}
            darfAnlegen={darf('rechnung', 'anlegen')}
            beiAuftrag={(id) => setzeMaske({ art: 'auftragsakte', id })}
            beiRechnung={(id) => setzeMaske({ art: 'rechnungsakte', id })}
          />
        </>
      )}

      {maske.art === 'rahmenliste' && (
        <>
          <Rahmenauftragsliste
            zurKunde={aktenwege.kunde}
            beiOeffnen={(id) => setzeMaske({ art: 'rahmenakte', id })}
            beiNeu={() => setzeMaske({ art: 'rahmenAnlegen' })}
          />
        </>
      )}

      {maske.art === 'rechnungsliste' && (
        <>
          <Rechnungsliste
            beiOeffnen={(id) => setzeMaske({ art: 'rechnungsakte', id })}
            zurKunde={aktenwege.kunde}
          />
        </>
      )}

      {maske.art === 'offeneposten' && (
        <>
          <OffenePosten
            zurKunde={aktenwege.kunde}
            beiRechnung={(id) => setzeMaske({ art: 'rechnungsakte', id })}
            darfBuchen={darf('rechnung', 'buchen')}
          />
        </>
      )}

      {maske.art === 'bgAbrechnung' && <BgAbrechnungsmaske />}

      {maske.art === 'rechnungsakte' && (
        <Rechnungsakte
          id={maske.id}
          /*
            RNG-04: Die Festschreibung haengt an `rechnung/buchen` und nicht an
            `aendern`. Das ist der Kern der Belegtrennung aus Tabelle 11 —
            Administration betreibt, Buchhaltung bucht.
          */
          darfAendern={darf('rechnung', 'aendern')}
          darfBuchen={darf('rechnung', 'buchen')}
          darfStornieren={darf('rechnung', 'stornieren')}
          darfVersenden={darf('rechnung', 'versenden')}
          darfLoeschen={darf('rechnung', 'loeschen')}
          beiKopf={setzeKopf}
          beiListe={() => setzeMaske({ art: 'rechnungsliste' })}
          beiRechnung={(neu) => setzeMaske({ art: 'rechnungsakte', id: neu })}
          wege={aktenwege}
        />
      )}

      {maske.art === 'benutzerliste' && (
        <Benutzerliste
          /*
            BEN-02: Ein eigenes Rechteobjekt fuer Benutzer gibt es nicht; die
            Verwaltung haengt durchgaengig an `einstellungen`.
          */
          darfPflegen={darf('einstellungen', 'konfigurieren')}
          beiBenutzer={(id) => setzeMaske({ art: 'benutzerakte', id })}
        />
      )}

      {/*
        CRM-117 (L6): die Terminakte. Teilnehmende an `buchung/sehen`,
        Benennen und Erfassen an `buchung/benennen` (0312), Check-in an
        `tagesplan/checkin` (TAG-62). Jeder Verweis nur mit dem Recht seiner
        Maske — sonst bleibt er Text (BEN-02).
      */}
      {maske.art === 'terminakte' && (
        <Terminakte
          id={maske.id}
          {...(darf('termin', 'sehen')
            ? { beiAbsageliste: () => setzeMaske({ art: 'absagen' }) }
            : {})}
          darfTeilnehmerSehen={darf('buchung', 'sehen')}
          darfBenennen={darf('buchung', 'benennen')}
          darfCheckin={darf('tagesplan', 'checkin')}
          darfCheckinStand={darf('tagesplan', 'arbeitsliste_sehen')}
          beiKopf={setzeKopf}
          {...(darf('buchung', 'sehen')
            ? { beiBuchung: (nummer: string) => setzeMaske({ art: 'buchungsvorgang', nummer }) }
            : {})}
          {...(darf('auftrag', 'sehen')
            ? { beiAuftrag: (id: string) => setzeMaske({ art: 'auftragsakte', id }) }
            : {})}
          {...(darf('kunde', 'sehen')
            ? { beiKunde: (id: string) => setzeMaske({ art: 'kundenakte', id }) }
            : {})}
          {...(darf('angebot', 'sehen')
            ? { beiAngebot: (id: string) => setzeMaske({ art: 'angebotsakte', id }) }
            : {})}
          {...(darf('tagesplan', 'sehen')
            ? {
                beiTagesplan: (tag: string, standort: string | undefined) =>
                  setzeMaske({
                    art: 'trainingsplanung',
                    tag,
                    ...(standort === undefined ? {} : { standort }),
                  }),
              }
            : {})}
        />
      )}

      {maske.art === 'benutzerakte' && (
        <Benutzerakte
          id={maske.id}
          darfPflegen={darf('einstellungen', 'konfigurieren')}
          beiListe={() => setzeMaske({ art: 'benutzerliste' })}
          beiKopf={setzeKopf}
        />
      )}

      {maske.art === 'bestaetigungen' && (
        <Bestaetigungen
          darfEntscheiden={darf('einstellungen', 'konfigurieren')}
          eigeneKennung={benutzer.id}
        />
      )}

      {/*
        BEN-07, SYS-05: Die eigenen Sitzungen und Geraete braucht JEDER —
        ohne Recht und ohne die Administration zu fragen. Wer sein Telefon im
        Zug liegen laesst, will nicht erst jemanden anrufen.
      */}
      {maske.art === 'meinkonto' && <MeinKonto />}
      {maske.art === 'systemzustand' && <Systemzustand />}
      {maske.art === 'buchungsvorgang' && (
        <Buchungsvorgang
          {...(maske.nummer === undefined ? {} : { nummer: maske.nummer })}
          {...(maske.reiter === undefined ? {} : { reiter: maske.reiter })}
          {...(maske.filter === undefined ? {} : { filter: maske.filter })}
          {...(maske.meldung === undefined ? {} : { meldung: maske.meldung })}
          beiKopf={setzeKopf}
          /*
            Auftrag CRM-175: „Buchung anlegen" öffnet die eigene Seite des
            Anlegens; die Filter der Liste reisen mit.
          */
          beiAnlegen={(offen) => {
            if (offen) {
              setzeMaske({
                art: 'buchungAnlegen',
                ...(maske.filter === undefined ? {} : { filter: maske.filter }),
              });
            }
          }}
          /*
            Auftrag #36: Nummer und Reiter stehen in der Adresse. Ein Reiterwechsel
            ERSETZT den Eintrag — „zurück" führt aus der Akte, nicht durch jeden
            Reiter; eine andere Buchung legt einen neuen an.
          */
          beiReiter={(reiter) => {
            if (maske.nummer !== undefined) setzeMaske({ ...maske, nummer: maske.nummer, reiter }, true);
          }}
          darfHerabsetzen={darf('buchung', 'herabsetzen')}
          {...(darf('bgzuschuss', 'sehen')
            ? { bgGutscheine: { darfErfassen: darf('bgzuschuss', 'erfassen') } }
            : {})}
          {...(darf('termin', 'sehen')
            ? { beiTermin: (termin: string) => setzeMaske({ art: 'terminakte', id: termin }) }
            : {})}
          wege={aktenwege}
          /*
            Auftrag CRM-70: Die Filter der Liste reisen mit in die Buchung, damit
            der Menüpunkt „Buchungen" (seit CRM-95; vorher der Verweis „Alle
            Buchungen") und „zurück" sie wiederherstellen. Ein geänderter Filter ERSETZT
            den Eintrag wie ein Reiter; der Weg zurück zur Liste legt einen an.
          */
          beiBuchung={(nummer) =>
            setzeMaske({
              art: 'buchungsvorgang',
              nummer,
              ...(maske.filter === undefined ? {} : { filter: maske.filter }),
            })
          }
          beiListe={(filter, ersetzen) => {
            const gesetzt = gesetzteFilter(filter);
            setzeMaske(
              Object.keys(gesetzt).length === 0
                ? { art: 'buchungsvorgang' }
                : { art: 'buchungsvorgang', filter: gesetzt },
              ersetzen,
            );
          }}
        />
      )}
      {/*
        BUC-50/BUC-51 (Auftrag CRM-175): das Anlegen als eigene Seite. Abbrechen
        legt den Weg zur Liste mit ihren Filtern an, wie der Menüpunkt
        „Buchungen" (`buchungslisteVon`). Die neue Buchung ERSETZT den Eintrag:
        „zurück" führt aus ihr in die Liste und nicht in ein leeres Formular.
      */}
      {maske.art === 'buchungAnlegen' && (
        <Buchungsvorgang
          anlegen
          beiKopf={setzeKopf}
          beiAnlegen={(offen) => {
            if (!offen) setzeMaske(buchungslisteVon(maske));
          }}
          beiBuchung={(nummer, meldung) =>
            setzeMaske(
              {
                art: 'buchungsvorgang',
                nummer,
                ...(maske.filter === undefined ? {} : { filter: maske.filter }),
                ...(meldung === undefined ? {} : { meldung }),
              },
              true,
            )
          }
          wege={aktenwege}
        />
      )}
      {/*
        DIE ARBEITSLISTE DER DISPOSITION (ANG-38).

        Der Kunde in der Zeile fuehrt in den TAGESPLAN (Auftrag CRM-69, Ulf am
        06.10.2026: „statt des Angebots, soll sich der tagesplan öffnen"): Wer
        antwortet, will vorher sehen, wie der Wunschtag belegt ist. Er oeffnet
        den ersten offenen Wunschtag am Standort der Anfrage; jeder Wunschtag
        ist ein eigener Verweis. Ueber denselben Einstieg wie aus den Terminen
        (CRM-67/68), mit eigenem Verlaufseintrag — „zurück" fuehrt in die
        Anfragen.

        Das ANGEBOT bleibt erreichbar: Die Angebotsnummer daneben fuehrt in die
        Akte, denn was verkauft werden soll, steht im Beleg. Ohne
        `tagesplan/sehen` (BEN-02) fuehrt der Kunde wie bisher ins Angebot.
      */}
      {maske.art === 'optionsanfragen' && (
        <Optionsanfragen
          beiAngebot={(id) => {
            setzeMaske({ art: 'angebotsakte', id });
          }}
          {...(darf('tagesplan', 'sehen')
            ? {
                beiTagesplan: (tag: string, standort: string | undefined) =>
                  setzeMaske({
                    art: 'trainingsplanung',
                    tag,
                    ...(standort === undefined ? {} : { standort }),
                  }),
              }
            : {})}
        />
      )}
      {/*
        DIE ABSAGELISTE (BUC-129 bis BUC-132). Bearbeiten darf der Support mit
        `termin:umbuchen` (AeA-04); wer nur Termine sieht, sieht die Liste ohne
        Knöpfe.
      */}
      {maske.art === 'widerrufe' && (
        <Widerrufe
          darfBearbeiten={darf('buchung', 'stornieren')}
          /* Auftrag #36: aus dem Widerruf in den Reiter, in dem storniert wird. */
          beiBuchung={(nummer) => {
            setzeMaske({ art: 'buchungsvorgang', nummer, reiter: 'storno' });
          }}
        />
      )}
      {/* KUN-15: lesen mit kunde/sehen, ändern mit kunde/aendern; erledigen auch, wem sie zugewiesen ist. */}
      {maske.art === 'wiedervorlagen' && darf('kunde', 'sehen') && (
        <MeineWiedervorlagen
          key={maske.oeffnen ?? ''}
          {...(maske.oeffnen === undefined ? {} : { oeffnen: maske.oeffnen })}
          ich={benutzer.id}
          darfAendern={darf('kunde', 'aendern')}
          beiKunde={(id) => setzeMaske({ art: 'kundenakte', id })}
        />
      )}
      {/* SUP-54: lesen mit rezension/sehen, antworten mit rezension/beantworten. */}
      {maske.art === 'rezensionen' && darf('rezension', 'sehen') && (
        <Rezensionen darfBeantworten={darf('rezension', 'beantworten')} />
      )}
      {/*
        SUP-03: lesen mit support/sehen, Status und Uebernahme mit support/bearbeiten.
        SUP-09, SUP-10, SUP-12: zuordnen mit support/zuordnen, Interessent anlegen mit kunde/anlegen.
      */}
      {maske.art === 'posteingang' && darf('support', 'sehen') && (
        <Posteingang
          ich={benutzer.id}
          /* SUP-23: die Folgeaktionen öffnen die vorhandenen Masken; ohne Recht fehlt der Weg. */
          folgewege={{
            ...(darf('angebot', 'anlegen')
              ? {
                angebot: (f) =>
                  setzeMaske({
                    art: 'angebotAnlegen',
                    kunde: f.kunde,
                    folge: { vorgang: f.vorgang, postfach: f.postfach, folgeaktion: f.folgeaktion },
                  }),
              }
              : {}),
            ...(darf('angebot', 'sehen') ? { angebotOeffnen: (id: string) => setzeMaske({ art: 'angebotsakte', id }) } : {}),
            ...(darf('buchung', 'sehen')
              ? { buchung: (nummer: string, reiter) => setzeMaske({ art: 'buchungsvorgang', nummer, reiter }) }
              : {}),
            ...(darf('rechnung', 'sehen') ? { rechnung: (id: string) => setzeMaske({ art: 'rechnungsakte', id }) } : {}),
          }}
          darfBearbeiten={darf('support', 'bearbeiten')}
          darfZuordnen={darf('support', 'zuordnen')}
          darfKundeAnlegen={darf('kunde', 'anlegen')}
          beiKunde={darf('kunde', 'sehen') ? (id) => setzeMaske({ art: 'kundenakte', id }) : undefined}
          /*
            Auftrag CRM-188: Der Vorgang steht in der Adresse und öffnet im ganzen
            Arbeitsbereich. Die Maske bleibt dabei stehen (`schluesselVon`), damit
            der Rückweg Reiter, Filter, Suche und Rolllage der Liste wiederfindet.
          */
          vorgang={maske.vorgang}
          nachricht={maske.nachricht}
          postfach={maske.postfach}
          beiZiel={(ziel, weg) => {
            if (weg === 'zurueck') {
              window.history.back();
              return;
            }
            setzeMaske(
              ziel === null
                ? { art: 'posteingang' }
                : ziel.art === 'vorgang'
                  ? { art: 'posteingang', vorgang: ziel.id }
                  : { art: 'posteingang', nachricht: ziel.id },
              weg === 'ersetzen',
            );
          }}
          vorbelegung={postfachVorbelegung(rechte)}
        />
      )}
      {maske.art === 'absagen' && (
        <Absagen
          darfBearbeiten={darf('termin', 'umbuchen')}
          /* Auftrag #36: aus der Absage in den Reiter, in dem umgebucht wird. */
          beiBuchung={(nummer) => {
            setzeMaske({ art: 'buchungsvorgang', nummer, reiter: 'storno' });
          }}
          /* ÄW-DP-02: von der Absage zurück in die Akte ihres Termins. */
          {...(darf('termin', 'sehen')
            ? { beiTerminakte: (id: string) => setzeMaske({ art: 'terminakte', id }) }
            : {})}
        />
      )}
      {maske.art === 'arbeitsliste' && (
        <Arbeitsliste
          /*
            DER WEG LÄUFT VON DER FRIST ZUR BUCHUNG (B5).

            Die Liste gibt die Buchungsnummer weiter, nicht die Kennung: Sie steht
            auf dem Beleg des Kunden, und die Buchungsmaske sucht über sie
            (BUC-51). Der Bereich wechselt mit — sonst stünde die Menüzeile auf
            „Arbeitsliste", während rechts eine Buchung steht.
          */
          /* Auftrag #36: aus der Frist dorthin, wo die Namen nachgetragen werden (BUC-70). */
          beiBuchung={(nummer) => {
            setzeMaske({ art: 'buchungsvorgang', nummer, reiter: 'teilnehmer' });
          }}
        />
      )}
      {/*
        DER EMPFANG AM KURSTAG (TAG-73, BUC-53).

        Zwei Rechte, und beide trägt der Empfang seit 0006: `arbeitsliste_sehen`
        fürs Lesen, `checkin` für den Haken. Wer nur sehen darf, sieht die Liste
        ohne Knöpfe — die Geschäftsleitung soll den Tag lesen können, ohne am
        Tresen abzuhaken.
      */}
      {maske.art === 'empfang' && (
        <Empfang
          darfAbhaken={darf('tagesplan', 'checkin')}
          {...(darf('termin', 'sehen')
            ? { beiTerminakte: (id: string) => setzeMaske({ art: 'terminakte', id }) }
            : {})}
        />
      )}
      {/*
        ZWEI MENÜPUNKTE, EINE MASKE (Auftraggeber, 11.09.2026).

        „Assets" sind Fahrmodule, Räume, Fahrzeuge, Betriebsmittel und
        Sonstiges; „Trainer" sind Trainer und externe Dienstleister. RES-01
        verlangt EIN Modell für beides — deshalb derselbe Baustein mit einem
        anderen Kreis und nicht zwei Masken, von denen die zweite veraltet.
      */}
      {maske.art === 'assets' && (
        <Ressourcen
          kreis="assets"
          /* SYS-01, BEN-02 (Auftrag #32): „Ressource anlegen" nur mit `ressource:pflegen`. */
          darfPflegen={darf('ressource', 'pflegen')}
          /*
            RES-73: Die Akte eines externen Trainers zeigt Rahmenvereinbarung,
            Absagefristen und Honorar. Die Fristen gehören ins Tagesgeschäft
            (RES-87), die Beträge nicht — deshalb zwei getrennte Rechte.
          */
          darfHonorar={darf('artikel', 'honorar_sehen')}
          darfHonorarAendern={darf('artikel', 'honorar_aendern')}
        />
      )}
      {maske.art === 'trainerstamm' && (
        <Ressourcen
          kreis="trainer"
          darfPflegen={darf('ressource', 'pflegen')}
          darfHonorar={darf('artikel', 'honorar_sehen')}
          darfHonorarAendern={darf('artikel', 'honorar_aendern')}
          /* RES-76: Die Eingangsrechnung eines Trainers prüft die Buchhaltung. */
          darfEingangsrechnungPruefen={darf('rechnung', 'buchen')}
        />
      )}

      {/*
       * PRD-29: Sehen darf jeder mit `artikel/sehen` — die Bausteine sind
       * das Vokabular, in dem Produkte, Module und Tagesplan reden. Pflegen
       * darf nur, wer `artikel/konfigurieren` hat (SYS-10).
       */}
      {maske.art === 'bausteine' && (
        <Bausteine darfKonfigurieren={darf('artikel', 'konfigurieren')} />
      )}

      {/*
       * AeA-08: Sehen darf jeder mit `tagesplan/sehen` — wer am Tag arbeitet,
       * muss ihn lesen. Eine Vorlage auflegen darf nur Operations
       * (`tagesplan/planen`); das ist die Aufgabenteilung und keine Vorsicht.
       */}
      {maske.art === 'trainingsplanung' && (
        <Trainingsplanung
          /* Auftrag CRM-67: der Einstieg mit Tag aus den Terminen (TAG-29). */
          {...(maske.tag === undefined ? {} : { tag: maske.tag })}
          {...(maske.standort === undefined ? {} : { standort: maske.standort })}
          /*
            Auftrag CRM-68: Der geöffnete Tag steht in derselben Adresse —
            `/trainingsplanung` ist die Liste, `/trainingsplanung/<tag>/<standort>`
            der Tag. Kein zweiter Mechanismus neben dem Einstieg aus CRM-67.
          */
          beiTag={(tag, standort, ersetzen) =>
            setzeMaske(
              tag === null ? { art: 'trainingsplanung' } : { art: 'trainingsplanung', tag, standort },
              ersetzen,
            )
          }
          darfPlanen={darf('tagesplan', 'planen')}
          darfFreigeben={darf('tagesplan', 'freigeben')}
          darfAbsagen={darf('termin', 'absagen')}
          darfOptionBeantworten={darf('angebot', 'terminoption_freigeben')}
          darfVereinbarungAendern={darf('termin', 'aendern')}
          darfWartelisteSehen={darf('buchung', 'sehen')}
          beiTerminakte={(termin: string) => setzeMaske({ art: 'terminakte', id: termin })}
        />
      )}
      {/*
        RES-49: Anfragen darf, wer zuordnen darf. Sehen darf, wer Ressourcen
        sehen darf — wer am Telefon sagen soll, ob ein Kurs stattfindet, muss
        nachsehen können, ohne zu disponieren.
      */}
      {maske.art === 'besetzung' && (
        <Besetzung
          darfZuordnen={darf('ressource', 'zuordnen')}
          /*
            RES-73: Wer keine Honorare sehen darf, sieht in der Besetzung
            auch keine — weder die Vorschau noch das Feld, mit dem man
            abweicht. Der Riegel steht im Dienst; das hier blendet nur aus,
            was ohnehin abgewiesen würde.
          */
          darfHonorar={darf('artikel', 'honorar_sehen')}
        />
      )}
      {maske.art === 'honorare' && (
        <Honorartabelle darfAendern={darf('artikel', 'honorar_aendern')} />
      )}
      {maske.art === 'gebuehrenstaffeln' && <Gebuehrenstaffeln />}
      {maske.art === 'rechtstexte' && <Rechtstexte />}
      {/* SUP-20 */}
      {maske.art === 'textbausteine' && darf('support', 'sehen') && (
        <Textbausteine darfPflegen={darf('support', 'vorlagen')} />
      )}
      {/*
        LEG-10 bis LEG-12: Drei Rechte, drei Prüfungen. Der Werbewiderspruch
        hängt bewusst an `kunde:aendern` — er kommt am Telefon an, und wer ihn
        entgegennimmt, ist der Innendienst und nicht die Datenschutzbeauftragte.
      */}
      {/*
        KAPITEL 21: die Nachweise, die eine Betriebspruefung verlangt
        (BP-11, BP-12, BP-40, BP-42, BP-43). `auswertung/sehen` — BEN-02
        zaehlt die Rechteobjekte auf, und ein eigenes Prueferrecht waere ein
        Aenderungsantrag.
      */}
      {maske.art === 'betriebspruefung' && <Betriebspruefung />}

      {/* AUS-16 (Auftrag #24): die eigenen Berichte. */}
      {maske.art === 'berichtsliste' && (
        <Berichtsliste
          beiOeffnen={(id) => setzeMaske({ art: 'berichtsakte', id })}
          beiKopf={setzeKopf}
          /* Auftrag #29: Zielwerte pflegen (AeA-43). */
          darfZielePflegen={darf('auswertung', 'zielwerte_pflegen')}
        />
      )}
      {(maske.art === 'berichtNeu' || maske.art === 'berichtsakte') && (
        <Baukasten
          /* Ein anderer Bericht ist ein anderer Baukasten — nicht der Zustand des vorigen. */
          key={maske.art === 'berichtsakte' ? maske.id : 'neu'}
          id={maske.art === 'berichtsakte' ? maske.id : undefined}
          /* AUS-08: Der Export gibt Daten aus dem Haus — dafuer gibt es das Recht seit 0006. */
          darfExportieren={darf('auswertung', 'exportieren')}
          /* AeA-42: mit allen teilen nur mit dem eigenen Recht. */
          darfTeilen={darf('auswertung', 'teilen')}
          /* Auftrag CRM-47: „Neuer Bericht" aus einem gespeicherten heraus. */
          beiNeu={() => setzeMaske({ art: 'berichtNeu' })}
          /* AUS-07: der Sprung aus einem Beleg in seine Akte — nur, wo es eine gibt. */
          beiBeleg={(art, id, nummer) => {
            switch (art) {
              case 'rechnung':
                return () => setzeMaske({ art: 'rechnungsakte', id });
              case 'angebot':
                return () => setzeMaske({ art: 'angebotsakte', id });
              /* Auftrag CRM-43: Auftragseingang und -bestand (AUF-29). */
              case 'auftrag':
                return () => setzeMaske({ art: 'auftragsakte', id });
              case 'kunde':
                return () => setzeMaske({ art: 'kundenakte', id });
              case 'buchung':
                /* Auftrag #36: aus der Herkunft einer Zahl in die Übersicht der Buchung. */
                return nummer === null
                  ? undefined
                  : () => setzeMaske({ art: 'buchungsvorgang', nummer, reiter: 'uebersicht' });
              default:
                return undefined;
            }
          }}
          beiKopf={setzeKopf}
          /* Ein gespeicherter Entwurf bekommt seine Adresse — ohne zweiten Eintrag in „zurück". */
          beiGespeichert={(id) => setzeMaske({ art: 'berichtsakte', id }, true)}
          beiGeloescht={() => setzeMaske({ art: 'berichtsliste' }, true)}
        />
      )}
      {maske.art === 'datenschutz' && (
        <Datenschutz
          darfAuskunft={darf('betroffenenrecht', 'auskunft')}
          darfEinschraenken={darf('betroffenenrecht', 'einschraenken')}
          darfWerbestand={darf('kunde', 'aendern')}
        />
      )}
      {/*
        DAS TERMINFENSTER BRAUCHT ZWEI RECHTE (BEN-02).

        `termin/absagen` fuer die Absage (AeA-04, BUC-138) und
        `angebot/terminoption_freigeben` fuer die Antwort auf eine
        Terminoption (ANG-32). Wer sie nicht hat, sieht den Abschnitt nicht —
        und keinen Knopf, der zuverlaessig scheitert.
      */}
      {maske.art === 'termine' && (
        <Termine
          /*
            SYS-01, BEN-02 (Auftrag #32): Anlegen und Verlegen an ihren
            Rechten — wer nur sieht, bekommt keinen Knopf.
          */
          darfAnlegen={darf('termin', 'anlegen')}
          darfVerlegen={darf('termin', 'verlegen')}
          darfAbsagen={darf('termin', 'absagen')}
          darfOptionBeantworten={darf('angebot', 'terminoption_freigeben')}
          darfVereinbarungAendern={darf('termin', 'aendern')}
          darfWartelisteSehen={darf('buchung', 'sehen')}
          beiTerminakte={(termin: string) => setzeMaske({ art: 'terminakte', id: termin })}
          /*
            Auftrag CRM-67: Die Ansicht steht in der Adresse und ERSETZT den
            Eintrag wie ein Reiter — „zurück" führt aus den Terminen, nicht
            durch jede Ansicht. Der Sprung in den Tagesplan legt einen neuen
            an, damit „zurück" wieder hierher führt. Ohne `tagesplan/sehen`
            gibt es ihn nicht (BEN-02), und das Datum bleibt Text.
          */
          /*
            Ohne Ansicht in der Adresse wählt die Maske die Vorgabe selbst —
            am Desktop der Kalender, auf dem Telefon die Tage (Auftrag CRM-106).
          */
          {...(maske.ansicht === undefined ? {} : { ansicht: maske.ansicht })}
          beiAnsicht={(ansicht) => setzeMaske({ art: 'termine', ansicht }, true)}
          {...(darf('tagesplan', 'sehen')
            ? {
                beiTagesplan: (tag: string, standort: string | undefined) =>
                  setzeMaske({
                    art: 'trainingsplanung',
                    tag,
                    ...(standort === undefined ? {} : { standort }),
                  }),
              }
            : {})}
        />
      )}
      {/* TAG-25: Die Regelwerte pflegt die Administration — `einstellungen:konfigurieren`. */}
      {/* FRS-08: pflegen mit `einstellungen/konfigurieren`, wie die uebrige Verwaltung. */}
      {maske.art === 'kalender' && <Kalender darfPflegen={darf('einstellungen', 'konfigurieren')} />}
      {/* RES-01, TAG-01: wie Kalender und Geschäftsdaten — `einstellungen/konfigurieren` (BEN-02). */}
      {maske.art === 'standorte' && <Standorte darfPflegen={darf('einstellungen', 'konfigurieren')} />}
      {maske.art === 'einstellungen' && (
        <Einstellungen
          darfKonfigurieren={darf('einstellungen', 'konfigurieren')}
          /* E-74 (CRM-163): die Werteliste lesen verlangt bgzuschuss/sehen, wie die API. */
          darfKostentraegerartenSehen={darf('bgzuschuss', 'sehen')}
        />
      )}
      {/* Kap. 29: Die Uebernahme ist Arbeit der Administration (MIG-08). */}
      {maske.art === 'uebernahme' && (
        <Uebernahme darfUebernehmen={darf('einstellungen', 'konfigurieren')} />
      )}

      {maske.art === 'geschaeftsdaten' && (
        <Geschaeftsdaten
          /*
            Firmierung, Steuernummer und Bankverbindung des Hauses stehen auf
            jedem Beleg, der hinausgeht. Wer sie ändert, ändert den Auftritt
            des Unternehmens — dasselbe Recht wie bei den Postfächern (SYS-10).
          */
          darfPflegen={darf('einstellungen', 'konfigurieren')}
        />
      )}

      {maske.art === 'ki' && (
        /* KI-05, KI-06: Einschalten heißt, Daten an einen fremden Dienst zu geben — dasselbe Recht wie bei den Postfächern. */
        <Ki darfPflegen={darf('einstellungen', 'konfigurieren')} />
      )}

      {maske.art === 'erechnung' && (
        <Erechnung
          /*
            RNG-13: Die steuerliche Einordnung des Hauses und seine
            Bankverbindung sind Einstellungen, keine Belegarbeit — dasselbe
            Recht wie bei den Postfächern (SYS-10).
          */
          darfPflegen={darf('einstellungen', 'konfigurieren')}
        />
      )}

      {/* TAG-49: Die Tablets richtet ein, wer die Einstellungen pflegt. */}
      {maske.art === 'checkingeraete' && (
        <Checkingeraete darfPflegen={darf('einstellungen', 'konfigurieren')} />
      )}

      {maske.art === 'portalzugaenge' && (
        <Portalzugaenge
          /*
            BUC-29: Ein Portalzugang ist ein Umsystem und kein Benutzerkonto.
            Er traegt keine Rolle nach BEN-04 und kommt an keine Maske — er
            ruft von aussen herein und bekommt Ereignisse hinaus (BUC-28).
            Deshalb `umsystem/verwalten` und nicht `benutzer/verwalten`.
          */
          darfPflegen={darf('umsystem', 'verwalten')}
        />
      )}

      {maske.art === 'postfaecher' && (
        <Postfaecher
          /*
            SUP-19: Unter welcher Adresse das HAUS auftritt, ist eine andere
            Verantwortung als das Versenden eines einzelnen Belegs. Das Recht
            ist deshalb `einstellungen/konfigurieren` (SYS-10) und nicht ein
            Belegrecht.
          */
          darfPflegen={darf('einstellungen', 'konfigurieren')}
        />
      )}

      {maske.art === 'berichtsversand' && <Berichtsversanduebersicht />}
      {maske.art === 'navision' && (
        <Navision
          /*
            Kap. 9, SYS-10: In WELCHES Navision das Haus bucht, ist eine
            andere Verantwortung als das Schreiben einer Rechnung. Das Recht
            ist deshalb `einstellungen/konfigurieren` und kein Belegrecht —
            dieselbe Trennung wie bei den Postfächern.
          */
          darfPflegen={darf('einstellungen', 'konfigurieren')}
        />
      )}

      {maske.art === 'belegvorlagen' && (
        <Belegvorlagen
          /*
            ANG-12: Die GESTALTUNG gilt fuer jeden kuenftigen Beleg des Hauses
            und haengt deshalb an `einstellungen/vorlagen_pflegen` — nicht am
            Beleg. Wer ein Angebot drucken darf, aendert damit noch nicht den
            Briefkopf.
          */
          darfPflegen={darf('einstellungen', 'vorlagen_pflegen')}
        />
      )}

      {maske.art === 'rahmenAnlegen' && (
        <RahmenauftragAnlegen
          beiAngelegt={(id) => setzeMaske({ art: 'rahmenakte', id })}
          beiAbbruch={() => setzeMaske({ art: 'rahmenliste' })}
        />
      )}

      {maske.art === 'rahmenakte' && (
        <Rahmenauftragsakte
          zurKunde={aktenwege.kunde}
          id={maske.id}
          darfAendern={darf('auftrag', 'aendern')}
          /*
            AUF-25: Der Abruf LEGT EINEN AUFTRAG AN — deshalb haengt er an
            `auftrag/anlegen` und nicht an `auftrag/aendern`. Dass dabei ein
            Rahmen beruehrt wird, aendert nichts daran, was entsteht.
          */
          darfAbrufen={darf('auftrag', 'anlegen')}
          beiKopf={setzeKopf}
          beiListe={() => setzeMaske({ art: 'rahmenliste' })}
          beiAuftrag={(id) => setzeMaske({ art: 'auftragsakte', id })}
        />
      )}

      {maske.art === 'auftragAnlegen' && (
        <AuftragAnlegen
          {...(maske.kunde === undefined ? {} : { anfangsKunde: maske.kunde })}
          beiAngelegt={(id) => setzeMaske({ art: 'auftragsakte', id })}
          beiAbbruch={() => setzeMaske({ art: 'auftragsliste' })}
        />
      )}

      {maske.art === 'rechnungAnlegen' && (
        <NeueRechnung
          {...(maske.kunde === undefined ? {} : { anfangsKunde: maske.kunde })}
          beiAngelegt={(id) => setzeMaske({ art: 'rechnungsakte', id })}
          beiAbbruch={() => setzeMaske({ art: 'rechnungsliste' })}
        />
      )}

      {maske.art === 'auftragsakte' && (
        <Auftragsakte
          id={maske.id}
          /*
            DREI RECHTE STATT EINEM (0033): `aendern` bearbeitet den Beleg,
            `bestaetigen` macht ihn verbindlich, `uebersteuern` behauptet,
            eine Leistung sei erbracht oder berechnet. AUF-13 verlangt das
            dritte ausdruecklich als eigenes Recht.
          */
          darfAendern={darf('auftrag', 'aendern')}
          darfBestaetigen={darf('auftrag', 'bestaetigen')}
          darfLoeschen={darf('auftrag', 'loeschen')}
          darfUebersteuern={darf('auftrag', 'uebersteuern')}
          /*
            AUF-19: Wer die Rechnung anlegt, ist nicht, wer den Auftrag
            aendert — `rechnung/anlegen` und nicht `auftrag/aendern`.
          */
          darfFakturieren={darf('rechnung', 'anlegen')}
          darfBuchen={darf('rechnung', 'buchen')}
          darfHerabsetzen={darf('buchung', 'herabsetzen')}
          {...(darf('bgzuschuss', 'sehen')
            ? { bgGutscheine: { darfErfassen: darf('bgzuschuss', 'erfassen') } }
            : {})}
          beiKopf={setzeKopf}
          beiListe={() => setzeMaske({ art: 'auftragsliste' })}
          beiVersion={(neu) => setzeMaske({ art: 'auftragsakte', id: neu })}
          beiAngebot={(angebot) => setzeMaske({ art: 'angebotsakte', id: angebot })}
          beiRechnung={(neu) => setzeMaske({ art: 'rechnungsakte', id: neu })}
          wege={aktenwege}
        />
      )}

      {maske.art === 'kundeAnlegen' && (
        <KundeAnlegen
          anfangsName={maske.name ?? ''}
          beiAngelegt={(id) => setzeMaske({ art: 'kundenakte', id, ...mitEinstieg(maske) })}
          beiAbbruch={() => setzeMaske({ art: 'kundenliste', ...mitEinstieg(maske) })}
        />
      )}

      {maske.art === 'kundenakte' && (
        <Kundenakte
          id={maske.id}
          meldung={maske.meldung}
          beiKopf={setzeKopf}
          beiListe={() => setzeMaske({ art: 'kundenliste', ...mitEinstieg(maske) })}
          beiZusammenfuehren={() =>
            setzeMaske({ art: 'zusammenfuehren', ziel: maske.id, ...mitEinstieg(maske) })
          }
          /*
            Die Handlungen des Aktenkopfs (ÄA-41) an den Rechten ihrer Masken:
            ANG-01 `angebot/anlegen`, AUF-01 `auftrag/anlegen`. Der Kunde geht
            als Vorbelegung mit.
          */
          {...(darf('angebot', 'anlegen')
            ? { beiNeuesAngebot: () => setzeMaske({ art: 'angebotAnlegen', kunde: maske.id }) }
            : {})}
          {...(darf('auftrag', 'anlegen')
            ? { beiNeuerAuftrag: () => setzeMaske({ art: 'auftragAnlegen', kunde: maske.id }) }
            : {})}
          {...(darfDirektabrechnen
            ? { beiNeueRechnung: () => setzeMaske({ art: 'rechnungAnlegen', kunde: maske.id }) }
            : {})}
          beiAngebot={(angebot) => setzeMaske({ art: 'angebotsakte', id: angebot })}
          beiAuftrag={(auftrag) => setzeMaske({ art: 'auftragsakte', id: auftrag })}
          /* KUN-15: Wiedervorlagen am Kunden; die Akte selbst verlangt kunde/sehen. */
          wiedervorlagen={{ ich: benutzer.id, darfAendern: darf('kunde', 'aendern') }}
          beiRechnung={(rechnung) => setzeMaske({ art: 'rechnungsakte', id: rechnung })}
          beiBuchung={(nummer) => setzeMaske({ art: 'buchungsvorgang', nummer })}
          /* SUP-11: die E-Mail-Vorgänge des Kunden, geöffnet im Posteingang. */
          {...(darf('support', 'sehen')
            ? { kommunikation: { beiVorgang: (vorgang: string, postfach: string) =>
              setzeMaske({ art: 'posteingang', vorgang, postfach }) } }
            : {})}
          darfFreieGutschrift={darf('rechnung', 'stornieren')}
          darfBuchen={darf('rechnung', 'buchen')}
          zurKunde={aktenwege.kunde}
          {...(darf('bgzuschuss', 'sehen')
            ? { bgKennzeichen: { darfVerwalten: darfBgKennzeichenSetzen(darf) } }
            : {})}
        />
      )}

      {maske.art === 'webanfragen' && (
        <Webanfragen
          darfBearbeiten={darf('kunde', 'anlegen')}
          beiKunde={(id) => setzeMaske({ art: 'kundenakte', id })}
          zurKunde={aktenwege.kunde}
          beiAngebot={
            darf('angebot', 'anlegen')
              ? (kunde) => setzeMaske({ art: 'angebotAnlegen', kunde })
              : undefined
          }
        />
      )}

      {maske.art === 'kontozuordnungen' && (
        <Kontozuordnungen
          darfBearbeiten={darf('kunde', 'anlegen')}
          beiKunde={(id) => setzeMaske({ art: 'kundenakte', id })}
        />
      )}

      {maske.art === 'zusammenfuehren' && (
        <DublettenZusammenfuehren
          ziel={maske.ziel}
          beiFertig={(bis) =>
            setzeMaske({
              art: 'kundenakte',
              id: maske.ziel,
              ...mitEinstieg(maske),
              // FRS-16: die Frist als konkretes Datum mit Uhrzeit, nie als
              // Anzahl von Tagen.
              meldung: `Zusammengeführt. Rücknehmbar bis ${alsFrist(bis)}.`,
            })
          }
          beiAbbruch={() =>
            setzeMaske({ art: 'kundenakte', id: maske.ziel, ...mitEinstieg(maske) })
          }
        />
      )}

      {/*
        B3: EIN PLATZHALTER, DER SAGT, WAS DORT KOMMT.

        „Noch nicht gebaut" ist eine Sackgasse. Wer die Auswertungen anklickt — und
        das ist die Geschäftsleitung, deren Dashboard-Kachel „Deckungsbeitrag" genau
        dorthin springt —, soll lesen, was ihn dort erwartet und wann. Aus einem
        leeren Versprechen wird damit eine Ankündigung.

        Der Text steht an der Maske in `BEREICH_ZU_MASKE` und nicht hier: Er gehört
        zum Bereich und nicht zum Platzhalter.
      */}
      {maske.art === 'unfertig' && (
        /* Seitentyp Übersicht (ÄA-41): eine Karte, die ankündigt, was kommt. */
        <div className="uebersicht">
          <section className="karte ankuendigung" aria-labelledby="ankuendigung-titel">
            <span className="bereichsseite__kachel" aria-hidden="true">
              <Sinnbild name="chart-bar" />
            </span>
            <div className="ankuendigung__text">
              <h2 id="ankuendigung-titel">{maske.beschriftung} ist noch nicht gebaut</h2>
              {maske.kommt !== undefined && <p>{maske.kommt}</p>}
              <p className="hilfe">
                Welche Masken es gibt, legt das Pflichtenheft fest; die Reihenfolge
                des Bauens folgt dem Lastenheft.
              </p>
            </div>
          </section>
        </div>
      )}
      </AbTablet>
      </Auffangnetz>
      </SeitenkontextAnbieter>
    </Rahmen>
  );
}
